import L from 'leaflet';
import ToastNotification from "../utils/ToastNotification";
import ConfirmationModal from "../../common/ConfirmationModal";
import { ContestTaskRenderer } from "./ContestTaskRenderer.js";
import $ from 'jquery';
import jsQR from 'jsqr';

window.jquery = $
const API_HOST = $("meta[name=api-url]").attr("content");

const SCRIPT_VERSION = "2.0.0"; 

const getPointTypes = () => ({
  regular: { value: -1, label: I18n.t("website.contest_task.editor.point_types.regular"), icon: 'fa-location-dot' },
  take_off: { value: 0, label: I18n.t("website.contest_task.editor.point_types.take_off"), icon: 'fa-plane-departure' },
  sss: { value: 1, label: I18n.t("website.contest_task.editor.point_types.sss"), icon: 'fa-flag-checkered' },
  ess: { value: 2, label: I18n.t("website.contest_task.editor.point_types.ess"), icon: 'fa-flag' },
  goal: { value: 3, label: I18n.t("website.contest_task.editor.point_types.goal"), icon: 'fa-trophy' },
  start_line: { value: 4, label: I18n.t("website.contest_task.editor.point_types.start_line"), icon: 'fa-hourglass-start' },
  waypoint: { value: 5, label: I18n.t("website.contest_task.editor.point_types.waypoint"), icon: 'fa-location-pin' },
  mark: { value: 6, label: I18n.t("website.contest_task.editor.point_types.mark"), icon: 'fa-map-marker' },
  turning_mark: { value: 7, label: I18n.t("website.contest_task.editor.point_types.turning_mark"), icon: 'fa-turn-up' },
  gate: { value: 10, label: I18n.t("website.contest_task.editor.point_types.gate"), icon: 'fa-door-open' },
  finish_line: { value: 11, label: I18n.t("website.contest_task.editor.point_types.finish_line"), icon: 'fa-flag-checkered' }
});

const waypointIcon = L.icon({
  iconUrl:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fb/Map_pin_icon_green.svg/752px-Map_pin_icon_green.svg.png",
  iconRetinaUrl:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fb/Map_pin_icon_green.svg/752px-Map_pin_icon_green.svg.png",
  iconSize: [25, 35],
  iconAnchor: [12, 35],
  popupAnchor: [1, -34]
});

const startIcon = L.icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  iconRetinaUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

const endIcon = L.icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  iconRetinaUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

const handleIcon = L.divIcon({
  className: 'track-handle',
  html: '<div style="width:12px;height:12px;border:1px solid blue;border-radius:50%;background:rgba(0,0,255,0.3);"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6]
});

const invisibleHandleIcon = L.divIcon({
  className: 'track-handle-invisible',
  html: '<div style="width:12px;height:12px;"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6]
});

const arrowIcon = L.divIcon({
  className: 'track-end-arrow',
  html: '<div>&#8594;</div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12]
});

export class ContestTaskManager {
  constructor() {
    this.history = [];
    this.historyPointer = -1;

    this.map = null;
    this.waypoints = [];
    this.markers = [];
    this.renderer = null;

    this.confirmationModal = new ConfirmationModal();

    this.currentDefaultRadius = 250;
    this.trackMode = false;
    this.trackHandles = [];

    // Altitude fetching
    this.altitudeFetchTimeouts = new Map(); // Store timeouts by waypoint index
    this.waypointAltitudes = new Map(); // Store altitudes by waypoint index
    this.currentEditingWaypointIndex = null; // Track which waypoint is being edited
    this.lastToggledWaypointIndex = null; // Track which waypoint was just added/removed from route
    this.userGroups = []; // Store user's groups

    this.initMap().then(async () => {
      // Fetch user's groups first before building the menu
      await this.fetchUserGroups();

      this.initMenu();
      this.initialize();
    });
  }

  async initialize() {
    const path = window.location.pathname;
    const matches = path.match(/\/contest_task\/(\d+)$/);
    if (matches) {
      this.isEditMode = true;
      this.taskId = matches[1];
      await this.loadExistingTask();
    } else if (window.data && window.data.track_waypoints) {
      // Load waypoints from track
      await this.loadTrackWaypoints(window.data.track_waypoints);
    }

    document.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        this.undo();
      }
    });

    const startInput = document.getElementById('startTimeBeginsInput');
    const endInput = document.getElementById('startTimeEndsInput');
    const goalInput = document.getElementById('goalDeadlineTimeInput');

    if (startInput && endInput && goalInput) {
      startInput.addEventListener('input', () => {
        endInput.value = startInput.value;
        goalInput.value = startInput.value;
      });
    }
  }

  validateTimeInputs(showToast = false) {
    const startInput = document.getElementById('startTimeBeginsInput');
    const endInput = document.getElementById('startTimeEndsInput');
    const goalInput = document.getElementById('goalDeadlineTimeInput');

    if (!startInput || !endInput || !goalInput) return true;

    // If any field is empty, don't validate (allow partial input)
    if (!startInput.value || !endInput.value || !goalInput.value) {
      return true;
    }

    const startTime = new Date(startInput.value);
    const endTime = new Date(endInput.value);
    const goalTime = new Date(goalInput.value);

    // Check if dates are valid Date objects
    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime()) || isNaN(goalTime.getTime())) {
      if (showToast) {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.dates_invalid"));
      }
      return false;
    }

    // Validate: Start Time Begins < Start Time Ends <= Goal Deadline Time
    if (startTime >= endTime) {
      if (showToast) {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.start_before_end"));
      }
      return false;
    }

    if (endTime > goalTime) {
      if (showToast) {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.end_before_goal"));
      }
      return false;
    }

    return true;
  }

  async fetchUserGroups() {
    // Only fetch if user is logged in
    if (!window.data?.user_logged_in || !window.data?.user_id) {
      return;
    }

    // Get the current category ID
    const categoryId = window.data?.category_id || 1;

    try {
      const response = await fetch(`${API_HOST}/v1/group?user_id=${window.data.user_id}&category_id=${categoryId}`, {
        headers: {
          'X-STL-Token': cookies.get('STL-Token')
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.userGroups = data.groups || [];
      }
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
      this.userGroups = [];
    }
  }

  updateGroupDropdown() {
    const existingGroupRow = document.getElementById('contest_task_group')?.closest('.form-group');
    const categoryRow = document.getElementById('contest_task_activity')?.closest('.form-group');

    if (!categoryRow) return;

    // Get current selected group value
    const currentGroupId = document.getElementById('contest_task_group')?.value || '';

    // Remove existing group dropdown if it exists
    if (existingGroupRow) {
      existingGroupRow.remove();
    }

    // If we have groups, add the dropdown
    if (this.userGroups.length > 0) {
      const groupOptions = [
        `<option value="">${I18n.t("website.contest_task.editor.form.no_group")}</option>`,
        ...this.userGroups.map(group =>
          `<option value="${group.id}" ${group.id == currentGroupId ? 'selected' : ''}>${group.name}</option>`
        )
      ].join('');

      const groupRowHtml = `
        <div class="form-group row align-items-center mb-0">
          <label for="contest_task_group" class="col-5 col-form-label fs-13">${I18n.t("website.contest_task.editor.form.assigned_group")}:</label>
          <div class="col-7">
            <select id="contest_task_group" class="form-control form-control-sm fs-13" name="group">
              ${groupOptions}
            </select>
          </div>
        </div>
      `;

      // Insert after category row
      categoryRow.insertAdjacentHTML('afterend', groupRowHtml);
    }
  }

  isWaypointInRoute(waypointIndex) {
    if (!this.trackMode || !this.trackHandles) {
      return false;
    }
    return this.trackHandles.some(handle => handle.attachedWaypointIndex === waypointIndex);
  }

  // Debounced altitude fetching
  fetchAltitudeForWaypoint(waypointIndex, latlng, delay = 100) {
    // Clear any existing timeout for this waypoint
    if (this.altitudeFetchTimeouts.has(waypointIndex)) {
      clearTimeout(this.altitudeFetchTimeouts.get(waypointIndex));
    }

    // Set up debounced fetch
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(`${API_HOST}/v1/contest_task/altitude?latitude=${latlng.lat}&longitude=${latlng.lng}`, {
          headers: {
            'X-STL-Token': cookies.get('STL-Token')
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.waypointAltitudes.set(waypointIndex, data.altitude);

          // Update altitude field if properties dialog is open for this waypoint
          const altInput = document.getElementById('waypointAltInput');
          if (altInput && this.currentEditingWaypointIndex === waypointIndex) {
            altInput.value = data.altitude;
          }
        } else {
          console.warn('Failed to fetch altitude:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching altitude:', error);
      } finally {
        this.altitudeFetchTimeouts.delete(waypointIndex);
      }
    }, delay);

    this.altitudeFetchTimeouts.set(waypointIndex, timeoutId);
  }

  getWaypointAltitude(waypointIndex) {
    return this.waypointAltitudes.get(waypointIndex) || 1000;
  }

  attachStartTimeSync() {
    const startInput = document.getElementById('startTimeBeginsInput');
    const endInput = document.getElementById('startTimeEndsInput');
    const goalInput = document.getElementById('goalDeadlineTimeInput');
    if (startInput && endInput && goalInput) {
      startInput.addEventListener('input', () => {
        const datePart = startInput.value.split('T')[0];
        const endTime = endInput.value.split('T')[1] || '12:00';
        const goalTime = goalInput.value.split('T')[1] || '18:00';

        endInput.value = `${datePart}T${endTime}`;
        goalInput.value = `${datePart}T${goalTime}`;

        // Don't show toast during input, only validate silently
        this.validateTimeInputs(false);
      });

      // Show toast only on blur (when user finishes editing)
      startInput.addEventListener('blur', () => {
        this.validateTimeInputs(true);
      });

      endInput.addEventListener('blur', () => {
        this.validateTimeInputs(true);
      });

      goalInput.addEventListener('blur', () => {
        this.validateTimeInputs(true);
      });
    }
  }

  saveHistory() {
    this.history = this.history.slice(0, this.historyPointer + 1);
    this.history.push({
      waypoints: this.waypoints.map(wp => L.latLng(wp.lat, wp.lng)),
      markers: this.markers.map(m => ({
        latlng: m.marker.getLatLng(),
        type: m.marker.waypointType,
        radius: m.circle ? m.circle.getRadius() : 0,
        label: m.label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1]
      }))
    });
    this.historyPointer++;
  }

  undo() {
    if (this.historyPointer <= 0) return;
    this.historyPointer--;
    const state = this.history[this.historyPointer];
    if (!state) return;
  
    if (this.trackHandles) this.trackHandles.forEach(h => this.map.removeLayer(h));
    this.trackHandles = [];
    while (this.waypoints.length > 0) this.deleteWaypoint(0);
  
    state.markers.forEach((m, i) => {
      this.addWaypoint(m.latlng, m.type);
      this.markers[i].circle.setRadius(m.radius);
      const newLabelIcon = L.divIcon({
        className: 'waypoint-label',
        html: `<div class="waypoint-text">${m.label}</div>`,
        iconSize: [40, 20],
        iconAnchor: [20, 40]
      });
      this.markers[i].label.setIcon(newLabelIcon);
    });

    this.trackHandles = this.waypoints.map((latlng, idx) => {
      const isEndpoint = idx === 0 || idx === this.waypoints.length - 1;
      const icon = isEndpoint ? invisibleHandleIcon : handleIcon;
      const handle = L.marker(latlng, {
        icon: icon,
        draggable: !isEndpoint
      }).addTo(this.map);
      handle.attachedWaypointIndex = idx;
      handle.autoInserted = false;
      if (!isEndpoint) this.attachHandleEvents(handle);
      return handle;
    });
  
    this.updateAutoHandles();
  
    this.updateRouteDrawing();
    this.showRouteWaypoints();
    this.updateMenu();
  }

  getTypeFromPointItemType(pointItemType) {
    const typeMapping = {
      0: 'take_off',
      1: 'sss',
      2: 'ess',
      3: 'goal',
      4: 'start_line',
      5: 'waypoint',
      6: 'mark',
      7: 'turning_mark',
      10: 'gate',
      11: 'finish_line'
    };
    return typeMapping[pointItemType] || 'waypoint';
  }

  getTypeFromServerType(serverType) {
    const typeMapping = {
      'REGULAR': 'regular',
      'TAKEOFF': 'take_off',
      'SSS': 'sss',
      'ESS': 'ess',
      'GOAL': 'goal',
      'START_LINE': 'start_line',
      'WAYPOINT': 'waypoint',
      'MARK': 'mark',
      'TURNING_MARK': 'turning_mark',
      'GATE': 'gate',
      'FINISH_LINE': 'finish_line'
    };
    return typeMapping[serverType] || 'waypoint';
  }

  async loadExistingTask() {
    try {
      const response = await fetch(`${API_HOST}/v1/contest_task/${this.taskId}`, {
        headers: {
          'X-STL-Token': cookies.get('STL-Token')
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load task');
      }

      const data = await response.json();
      const { contest_task: taskData, points } = data;

      // Update window.data with task's group_id for form rebuilding
      if (window.data) {
        window.data.group_id = taskData.group_id || null;
      }

      this.trackMode = true;

      let menuContent = document.getElementById('menu-content');
      if (!document.getElementById('taskNameInput')) {
        let settingsDiv = document.createElement("div");
        settingsDiv.className = "task-settings visible rounded pt-2 pb-2";
        settingsDiv.innerHTML = `
          <label>${I18n.t("website.contest_task.editor.form.task_name")}</label>
          <input id="taskNameInput" type="text">
          <div class="time-inputs">
            <div>
              <label>${I18n.t("website.contest_task.editor.form.start_time_begins")}</label>
              <input id="startTimeBeginsInput" type="datetime-local">
            </div>
            <div>
              <label>${I18n.t("website.contest_task.editor.form.start_time_ends")}</label>
              <input id="startTimeEndsInput" type="datetime-local">
            </div>
            <div>
              <label>${I18n.t("website.contest_task.editor.form.goal_deadline_time")}</label>
              <input id="goalDeadlineTimeInput" type="datetime-local">
            </div>
            <div>
              <label>${I18n.t("website.contest_task.editor.form.target_speed")}</label>
              <input id="targetSpeedInput" type="number" step="0.1" min="0" placeholder="7.0">
            </div>
          </div>
        `;
        menuContent.insertBefore(settingsDiv, menuContent.firstChild);
      }


      document.getElementById('taskNameInput').value = taskData.name || '';
      document.getElementById('startTimeBeginsInput').value = taskData.start_time_begins?.split('.')[0] || '';
      document.getElementById('startTimeEndsInput').value = taskData.start_time_ends?.split('.')[0] || '';
      document.getElementById('goalDeadlineTimeInput').value = taskData.goal_deadline_time?.split('.')[0] || '';
      document.getElementById('targetSpeedInput').value = taskData.target_speed || '7.0';

      // Check if dates are set and enable date fields if they are
      const hasDates = taskData.start_time_begins || taskData.start_time_ends || taskData.goal_deadline_time;
      if (hasDates) {
        this.dateFieldsEnabled = true;
        // Show date fields immediately
        this.toggleDateFields();
      }

      // Attach event listeners for date validation
      this.attachStartTimeSync();

      if (this.trackHandles) {
        this.trackHandles.forEach(handle => this.map.removeLayer(handle));
        this.trackHandles = [];
      }
      this.waypoints.forEach((_, index) => {
        this.deleteWaypoint(0);
      });

      
      const bounds = L.latLngBounds([]);
      const newHandles = [];

      // Separate points into route waypoints and markers
      // Default to route waypoint if field is missing (backwards compatibility)
      const routePoints = points.filter(point => point.is_route_waypoint !== false);
      const markerPoints = points.filter(point => point.is_route_waypoint === false);

      // Add all waypoints first (both route and markers)
      points.forEach((point, index) => {
        const latlng = L.latLng(parseFloat(point.latitude), parseFloat(point.longitude));
        bounds.extend(latlng);

        const type = this.getTypeFromServerType(point.type_id);

        const wpIndex = this.addWaypoint(latlng, type, true); // Skip altitude fetch, we already have it
        const marker = this.markers[wpIndex];

        // Set waypoint ID if available
        if (point.waypoint_id) {
          marker.marker.waypointId = point.waypoint_id;
        }

        // Store altitude if available, otherwise fetch it
        const hasValidAltitude = point.altitude &&
                                 point.altitude !== null &&
                                 point.altitude !== undefined &&
                                 !isNaN(point.altitude) &&
                                 (point.altitude !== 1000 || (point.altitude % 1 !== 0));

        if (hasValidAltitude) {
          // Valid altitude data exists, store it
          this.waypointAltitudes.set(wpIndex, point.altitude);
        } else {
          // No valid altitude data, fetch it from API
          this.fetchAltitudeForWaypoint(wpIndex, latlng, 100);
        }

        const newLabelIcon = L.divIcon({
          className: 'waypoint-label',
          html: `<div class="waypoint-text">${point.name}</div>`,
          iconSize: [40, 20],
          iconAnchor: [20, 40]
        });
        marker.label.setIcon(newLabelIcon);

        if (marker.circle) {
          marker.circle.setRadius(point.radius);
        }
      });

      // Create handles only for route waypoints
      routePoints.forEach((point, routeIndex) => {
        // Find the waypoint index in the full waypoints array
        const waypointIndex = points.findIndex(p =>
          p.latitude === point.latitude &&
          p.longitude === point.longitude &&
          p.name === point.name
        );

        if (waypointIndex !== -1) {
          const latlng = this.waypoints[waypointIndex];
          const isEndpoint = routeIndex === 0 || routeIndex === routePoints.length - 1;

          const handle = L.marker(latlng, {
            icon: isEndpoint ? invisibleHandleIcon : handleIcon,
            draggable: !isEndpoint
          }).addTo(this.map);
          handle.attachedWaypointIndex = waypointIndex;
          handle.autoInserted = false;

          if (!isEndpoint) {
            this.attachHandleEvents(handle);
          }
          newHandles.push(handle);
        }
      });

      this.trackHandles = newHandles;
      this.updateAutoHandles();
      
      
      this.map.fitBounds(bounds, {
        padding: [20, 20]
      });
      
      const addTrackBtn = document.getElementById('addTrackBtn');
      const saveTask = document.getElementById('saveTask');
      const exportTask = document.getElementById('exportTask');
      const setDateBtn = document.getElementById('setDate');

      if (addTrackBtn) {
        addTrackBtn.style.display = 'none';
      }

      // Only show save button if user can save (owner or new task)
      if (saveTask && window.data.user_can_save_task) {
        saveTask.style.display = 'block';
      }

      if (exportTask) {
        exportTask.style.display = 'block';
      }

      if (setDateBtn) {
        setDateBtn.style.display = 'inline-block';
      }

      this.updateRouteDrawing();
      this.showRouteWaypoints();
      this.updateMenu();

      // Store original task data AFTER everything is loaded
      // This ensures the format matches exactly what buildContestTaskData() will produce
      this.originalTaskData = this.buildContestTaskData().contest_task;

    } catch (error) {
      console.error('Error loading task:', error);
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.failed_load_task"));
    }
  }

  async loadTrackWaypoints(waypoints) {
    try {
      this.trackMode = true;

      // Show toast notification for new challenge creation
      ToastNotification.make(
        I18n.t("website.contest_task.editor.toast_messages.create_challenge_from_track")
      );

      // Clear any existing waypoints
      if (this.trackHandles) {
        this.trackHandles.forEach(handle => this.map.removeLayer(handle));
        this.trackHandles = [];
      }
      this.waypoints.forEach((_, index) => {
        this.deleteWaypoint(0);
      });

      const bounds = L.latLngBounds([]);
      const newHandles = [];

      // Add waypoints from track
      waypoints.forEach((point, routeIndex) => {
        const latlng = L.latLng(parseFloat(point.latitude), parseFloat(point.longitude));
        bounds.extend(latlng);

        const type = this.getTypeFromServerType(point.type_id);
        const wpIndex = this.addWaypoint(latlng, type, true);
        const marker = this.markers[wpIndex];

        // Store altitude
        if (point.altitude && !isNaN(point.altitude)) {
          this.waypointAltitudes.set(wpIndex, point.altitude);
        }

        // Set label
        const newLabelIcon = L.divIcon({
          className: 'waypoint-label',
          html: `<div class="waypoint-text">${point.name}</div>`,
          iconSize: [40, 20],
          iconAnchor: [20, 40]
        });
        marker.label.setIcon(newLabelIcon);

        if (marker.circle) {
          marker.circle.setRadius(point.radius);
        }

        // Create handle for route waypoint
        const isEndpoint = routeIndex === 0 || routeIndex === waypoints.length - 1;
        const handle = L.marker(latlng, {
          icon: isEndpoint ? invisibleHandleIcon : handleIcon,
          draggable: !isEndpoint
        }).addTo(this.map);
        handle.attachedWaypointIndex = wpIndex;
        handle.autoInserted = false;

        if (!isEndpoint) {
          this.attachHandleEvents(handle);
        }
        newHandles.push(handle);
      });

      this.trackHandles = newHandles;
      this.updateAutoHandles();

      this.map.fitBounds(bounds, {
        padding: [20, 20]
      });

      // Show UI buttons
      const addTrackBtn = document.getElementById('addTrackBtn');
      const saveTask = document.getElementById('saveTask');
      const exportTask = document.getElementById('exportTask');
      const setDateBtn = document.getElementById('setDate');

      if (addTrackBtn) {
        addTrackBtn.style.display = 'none';
      }
      if (saveTask && window.data.user_can_save_task) {
        saveTask.style.display = 'block';
      }
      if (exportTask) {
        exportTask.style.display = 'block';
      }
      if (setDateBtn) {
        setDateBtn.style.display = 'inline-block';
      }

      this.updateRouteDrawing();
      this.showRouteWaypoints();
      this.updateMenu();

    } catch (error) {
      console.error('Error loading track waypoints:', error);
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.failed_load_track"));
    }
  }

  updateMenu() {
    this.showRouteWaypoints();
    const resetTaskBtn = document.getElementById('resetTask');
    if (resetTaskBtn) {
      if (this.trackHandles && this.trackHandles.length > 0) {
        resetTaskBtn.style.display = 'block';
      } else {
        resetTaskBtn.style.display = 'none';
      }
    }
    this.ensureMenuOpen();
  }

  toggleDateFields() {
    const setDateBtn = document.getElementById('setDate');
    const startTimeBeginsRow = document.getElementById('startTimeBeginsInput')?.closest('.form-group');
    const startTimeEndsRow = document.getElementById('startTimeEndsInput')?.closest('.form-group');
    const goalDeadlineRow = document.getElementById('goalDeadlineTimeInput')?.closest('.form-group');

    if (this.dateFieldsEnabled) {
      // Add active state to button
      if (setDateBtn) {
        setDateBtn.classList.add('active');
        setDateBtn.classList.add('text-white');
        setDateBtn.classList.remove('bg-warning');
        setDateBtn.classList.add('bg-success');
      }
      // Show date fields
      if (startTimeBeginsRow) startTimeBeginsRow.style.display = '';
      if (startTimeEndsRow) startTimeEndsRow.style.display = '';
      if (goalDeadlineRow) goalDeadlineRow.style.display = '';
    } else {
      // Remove active state from button
      if (setDateBtn) {
        setDateBtn.classList.remove('active');
        setDateBtn.classList.remove('text-white');
        setDateBtn.classList.remove('bg-success');
        setDateBtn.classList.add('bg-warning');
      }
      // Hide date fields
      if (startTimeBeginsRow) startTimeBeginsRow.style.display = 'none';
      if (startTimeEndsRow) startTimeEndsRow.style.display = 'none';
      if (goalDeadlineRow) goalDeadlineRow.style.display = 'none';
    }
  }

  getTiles() {
    return [
      {
        id: 0,
        name: 'OSM-Carto Retina',
        url: 'https://tile.tracestrack.com/topo__/{z}/{x}/{y}.png?key=5b37c4cf109fa26755308c3a5e221369',
        options: {
          maxZoom: 17,
          attribution: 'Data: © OpenStreetMap contributors, SRTM, NASADEM; Maps © Tracestrack for SportsTrackLive'
        }
      },
      {
        id: 1,
        name: 'GoogleTerrain',
        url: 'https://mt{s}.google.com/vt/lyrs=p&hl=en&x={x}&y={y}&z={z}&s=Ga',
        options: {
          subdomains: '012',
          maxZoom: 20,
          attribution: ''
        }
      },
      {
        id: 2,
        name: 'GoogleSatellite',
        url: 'https://mt{s}.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}&s=Ga',
        options: {
          subdomains: '012',
          maxZoom: 20,
          attribution: ''
        }
      },
    ]
  }

  async initMap() {
    let center = [46.4237013, 11.8256556];
    if (!this.isEditMode) {
      center = await this.getUserLatLngFromIP();
    }

    // Get available tile layers
    const tiles = this.getTiles();
    const baseLayers = {};

    tiles.forEach((tile, index) => {
      const layer = L.tileLayer(tile.url, tile.options);
      baseLayers[tile.name] = layer;
    });

    // Use the first tile as default
    const defaultLayer = Object.values(baseLayers)[0];

    // Skyways overlay
    const skywaysLayer = L.tileLayer(
      'https://thermal.kk7.ch/tiles/skyways_all_all/{z}/{x}/{y}.png?src=' + window.location.hostname,
      {
        attribution: 'thermal.kk7.ch <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/">CC-BY-NC-SA</a>',
        maxNativeZoom: 13,
        tms: true
      }
    );

    // Thermals overlay
    const thermalsLayer = L.tileLayer(
      'https://thermal.kk7.ch/tiles/thermals_jul_07/{z}/{x}/{y}.png?src=' + window.location.hostname,
      {
        attribution: 'thermal.kk7.ch <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/">CC-BY-NC-SA</a>',
        maxNativeZoom: 12,
        tms: true
      }
    );

    this.map = L.map('map-contest', {
      doubleClickZoom: false,
      center: center,
      zoom: 9,
      layers: [defaultLayer],
      zoomControl: false,  // Disable default zoom control
      attributionControl: false  // Disable Leaflet attribution
    });

    // Store base layers and overlays for custom control
    this.baseLayers = baseLayers;
    this.overlayLayers = {
      "Skyways": skywaysLayer,
      "Thermals": thermalsLayer
    };

    // Create unified map tools control
    this.createUnifiedMapControl(baseLayers, {
      "Skyways": skywaysLayer,
      "Thermals": thermalsLayer
    });

    // Initialize the renderer
    this.renderer = new ContestTaskRenderer(this.map);

    // Initialize POI search after map controls are added
    setTimeout(() => {
      this.initPoiSearch();
    }, 100);
  
    this.map.on('contextmenu', (e) => {
      e.originalEvent.preventDefault();

      // Check if user can edit the task
      if (!window.data?.user_can_save_task) {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.not_owner"));
        return false;
      }

      if (!this.wptClusterGroup || !this.map.hasLayer(this.wptClusterGroup)) {
        this.addWaypoint(e.latlng);
        this.showRouteWaypoints();
        this.saveHistory();
      }
      return false;
    });

    this.map.on('dblclick', (e) => {
      // Check if user can edit the task
      if (!window.data?.user_can_save_task) {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.not_owner"));
        return;
      }

      this.addWaypoint(e.latlng);
    });
  }

  createUnifiedMapControl(baseLayers, overlays) {
    // Create custom control for map tools
    const MapToolsControl = L.Control.extend({
      options: {
        position: 'topleft'
      },

      onAdd: (map) => {
        const container = L.DomUtil.create('div', 'leaflet-control-layers leaflet-control');
        container.style.background = 'white';
        container.style.borderRadius = '4px';
        container.style.boxShadow = '0 1px 5px rgba(0,0,0,0.4)';

        // Create toggle button
        const toggleBtn = L.DomUtil.create('a', 'leaflet-control-layers-toggle', container);
        toggleBtn.href = '#';
        toggleBtn.title = 'Map Tools';
        toggleBtn.style.width = '36px';
        toggleBtn.style.height = '36px';
        toggleBtn.style.display = 'block';
        toggleBtn.style.backgroundImage = 'url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzNiIgaGVpZ2h0PSIzNiI+PHBhdGggZD0iTTEwIDExaDE2djJIMTB6bTAgNWgxNnYySDEwem0wIDVoMTZ2MkgxMHoiIGZpbGw9IiM1NTUiLz48L3N2Zz4=)';
        toggleBtn.style.backgroundPosition = '50% 50%';
        toggleBtn.style.backgroundRepeat = 'no-repeat';

        // Create content panel
        const content = L.DomUtil.create('div', 'leaflet-control-layers-content', container);
        content.style.display = 'none';
        content.style.padding = '10px';
        content.style.minWidth = '200px';
        content.style.maxHeight = '80vh';
        content.style.overflowY = 'auto';

        // Add Zoom Controls
        const zoomSection = L.DomUtil.create('div', 'mb-3', content);

        const zoomLevelDiv = L.DomUtil.create('div', 'text-center mb-2 text-muted small', zoomSection);
        const updateZoomLevel = () => {
          zoomLevelDiv.textContent = `Current: ${map.getZoom()}`;
        };
        updateZoomLevel();
        map.on('zoomend', updateZoomLevel);

        const zoomButtonsDiv = L.DomUtil.create('div', 'd-flex gap-2', zoomSection);
        zoomButtonsDiv.style.gap = '5px';

        const zoomInBtn = L.DomUtil.create('button', 'btn btn-sm btn-outline-secondary', zoomButtonsDiv);
        zoomInBtn.innerHTML = '<i class="fas fa-plus"></i> Zoom In';
        zoomInBtn.type = 'button';
        zoomInBtn.style.flex = '1';
        L.DomEvent.on(zoomInBtn, 'click', (e) => {
          L.DomEvent.preventDefault(e);
          map.zoomIn();
        });

        const zoomOutBtn = L.DomUtil.create('button', 'btn btn-sm btn-outline-secondary', zoomButtonsDiv);
        zoomOutBtn.innerHTML = '<i class="fas fa-minus"></i> Zoom Out';
        zoomOutBtn.type = 'button';
        zoomOutBtn.style.flex = '1';
        L.DomEvent.on(zoomOutBtn, 'click', (e) => {
          L.DomEvent.preventDefault(e);
          map.zoomOut();
        });

        // Add separator
        L.DomUtil.create('hr', '', content);

        // Add POI Search
        const poiSection = L.DomUtil.create('div', 'mb-3', content);
        poiSection.innerHTML = `
          <div class="mb-2 font-weight-bold">${I18n.t("website.contest.search_poi")}</div>
          <div id="poi-search-container" class="position-relative">
            <input type="text" id="poi-search-input" class="form-control form-control-sm border" placeholder="${I18n.t("website.contest.search_poi")}" style="width: 100%;" />
          </div>
        `;

        // Add separator
        L.DomUtil.create('hr', '', content);

        // Add base layers section
        const baseSection = L.DomUtil.create('div', 'mb-3', content);
        const baseTitle = L.DomUtil.create('div', 'mb-2 font-weight-bold', baseSection);
        baseTitle.textContent = I18n.t("website.contest.base_layers");

        const currentLayer = Object.values(baseLayers).find(layer => map.hasLayer(layer));

        Object.keys(baseLayers).forEach(name => {
          const label = L.DomUtil.create('label', 'd-block mb-1', baseSection);
          label.style.cursor = 'pointer';

          const radio = L.DomUtil.create('input', '', label);
          radio.type = 'radio';
          radio.name = 'base-layer';
          radio.checked = baseLayers[name] === currentLayer;
          radio.style.marginRight = '5px';

          L.DomEvent.on(radio, 'change', () => {
            Object.values(baseLayers).forEach(layer => map.removeLayer(layer));
            map.addLayer(baseLayers[name]);
          });

          label.appendChild(document.createTextNode(name));
        });

        // Add separator
        L.DomUtil.create('hr', '', content);

        // Add overlays section
        const overlaySection = L.DomUtil.create('div', '', content);
        const overlayTitle = L.DomUtil.create('div', 'mb-2 font-weight-bold', overlaySection);
        overlayTitle.textContent = 'Overlay';

        Object.keys(overlays).forEach(name => {
          const label = L.DomUtil.create('label', 'd-block mb-1', overlaySection);
          label.style.cursor = 'pointer';

          const checkbox = L.DomUtil.create('input', '', label);
          checkbox.type = 'checkbox';
          checkbox.checked = map.hasLayer(overlays[name]);
          checkbox.style.marginRight = '5px';

          L.DomEvent.on(checkbox, 'change', () => {
            if (checkbox.checked) {
              map.addLayer(overlays[name]);
            } else {
              map.removeLayer(overlays[name]);
            }
          });

          label.appendChild(document.createTextNode(name));
        });

        // Toggle functionality
        let isExpanded = false;
        L.DomEvent.on(toggleBtn, 'click', (e) => {
          L.DomEvent.preventDefault(e);
          L.DomEvent.stopPropagation(e);

          isExpanded = !isExpanded;
          content.style.display = isExpanded ? 'block' : 'none';

          if (isExpanded) {
            container.classList.add('leaflet-control-layers-expanded');
          } else {
            container.classList.remove('leaflet-control-layers-expanded');
          }
        });

        // Prevent map interactions when using control
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      }
    });

    this.map.addControl(new MapToolsControl());
  }

  updateMiddleHandlesNearWaypoint(waypointIndex) {
    if (!this.trackHandles || !this.trackHandles.length) return;
    
    
    this.trackHandles.forEach((handle, idx) => {
      if (handle.autoInserted) {
        const prev = this.trackHandles[idx - 1];
        const next = this.trackHandles[idx + 1];
        
        
        if ((prev && prev.attachedWaypointIndex === waypointIndex) ||
            (next && next.attachedWaypointIndex === waypointIndex)) {
          
          
          const prevPos = prev.getLatLng();
          const nextPos = next.getLatLng();
          const midLatLng = L.latLng(
            (prevPos.lat + nextPos.lat) / 2,
            (prevPos.lng + nextPos.lng) / 2
          );
          handle.setLatLng(midLatLng);
        }
      }
    });
  }

  openWaypointEditor(idx) {
    if (idx === -1 || idx >= this.markers.length) return;

    // Check if user can edit the task
    if (!window.data?.user_can_save_task) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.not_owner"));
      return;
    }

    const marker = this.markers[idx].marker;
    const type = marker.waypointType;
    const currentRadius = this.markers[idx].circle.getRadius();
    const currentLabelText = this.markers[idx].label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1];
    const isInRoute = this.isWaypointInRoute(idx);

    const popup = L.popup({
      closeButton: true,
      autoClose: true,
      closeOnClick: false,
      className: 'waypoint-properties-popup',
      maxWidth: 300,
      minWidth: 300
    })
      .setLatLng(marker.getLatLng())
      .setContent(`
        <div class="container-fluid p-0" class="fs-12">
          <h2 class="mb-3">${I18n.t("website.contest_task.editor.dialog.edit_waypoint")}</h2>
          <form>
            <div class="form-group row align-items-center mb-0">
              <label for="waypointNameInput" class="col-5 col-form-label">${I18n.t("website.contest_task.editor.dialog.name")}</label>
              <div class="col-7">
                <input id="waypointNameInput" type="text" class="form-control form-control-sm" value="${currentLabelText}">
              </div>
            </div>
            <div class="form-group row align-items-center mb-0">
              <label for="waypointIdInput" class="col-5 col-form-label">${I18n.t("website.contest_task.editor.dialog.id")}</label>
              <div class="col-7">
                <input id="waypointIdInput" maxlength="10" type="text" class="form-control form-control-sm" style="text-transform: uppercase;" value="${marker.waypointId || ''}">
              </div>
            </div>
            <div class="form-group row align-items-center mb-0">
              <label for="waypointTypeSelect" class="col-5 col-form-label">${I18n.t("website.contest_task.editor.dialog.type")}</label>
              <div class="col-7">
                <select id="waypointTypeSelect" class="form-control form-control-sm">
                  ${Object.entries(getPointTypes()).map(([key, type]) => `
                    <option value="${key}" ${marker.waypointType === key ? 'selected' : ''}>
                      ${type.label}
                    </option>
                  `).join('')}
                </select>
              </div>
            </div>
            <div class="form-group row align-items-center mb-0">
              <label for="waypointLatInput" class="col-5 col-form-label">${I18n.t("website.contest_task.editor.dialog.latitude")}</label>
              <div class="col-7">
                <input id="waypointLatInput" type="number" step="0.0001" class="form-control form-control-sm" value="${marker.getLatLng().lat}">
              </div>
            </div>
            <div class="form-group row align-items-center mb-0">
              <label for="waypointLngInput" class="col-5 col-form-label">${I18n.t("website.contest_task.editor.dialog.longitude")}</label>
              <div class="col-7">
                <input id="waypointLngInput" type="number" step="0.0001" class="form-control form-control-sm" value="${marker.getLatLng().lng}">
              </div>
            </div>
            <div class="form-group row align-items-center mb-0">
              <label for="waypointAltInput" class="col-5 col-form-label">${I18n.t("website.contest_task.editor.dialog.altitude")}</label>
              <div class="col-7">
                <input id="waypointAltInput" type="number" class="form-control form-control-sm" value="${this.getWaypointAltitude(idx)}" readonly style="background-color: #f8f9fa; color: #6c757d;">
              </div>
            </div>
            <div class="form-group row align-items-center mb-0">
              <label for="waypointRadiusInput" class="col-5 col-form-label">${I18n.t("website.contest_task.editor.dialog.radius")}</label>
              <div class="col-7">
                <input id="waypointRadiusInput" type="number" class="form-control form-control-sm" step="25" min="50" value="${currentRadius}">
              </div>
            </div>
            <div class="d-flex gap-1 mb-2 mt-3 justify-content-end">
            ${type !== 'take_off' && type !== 'goal' ?
              `<button id="deleteWaypointBtn" type="button" class="btn btn-danger btn-sm mr-1">${I18n.t("website.contest_task.editor.dialog.delete")}</button>`:
              ''}
              <button id="toggleRouteBtn" type="button" class="btn btn-warning btn-sm mr-1">${isInRoute ? I18n.t("website.contest_task.editor.dialog.remove_from_route") : I18n.t("website.contest_task.editor.dialog.add_to_route")}</button>
              <button id="updateWaypointBtn" type="button" class="btn btn-success btn-sm mr-1">${I18n.t("website.contest_task.editor.dialog.update")}</button>
            </div>
          </form>
        </div>
      `)
      .openOn(this.map);

    // Track which waypoint is being edited and fetch altitude
    this.currentEditingWaypointIndex = idx;
    this.fetchAltitudeForWaypoint(idx, marker.getLatLng(), 0); // Immediate fetch for dialog

    setTimeout(() => {
      // Store cleanup function so we can call it when popup closes
      let cleanupFn = null;
      const deleteBtn = document.getElementById('deleteWaypointBtn');
      const updateBtn = document.getElementById('updateWaypointBtn');
      const toggleRouteBtn = document.getElementById('toggleRouteBtn');
      const nameInput = document.getElementById('waypointNameInput');
      const idInput = document.getElementById('waypointIdInput');
      const typeSelect = document.getElementById('waypointTypeSelect');
      const latInput = document.getElementById('waypointLatInput');
      const lngInput = document.getElementById('waypointLngInput');
      const altInput = document.getElementById('waypointAltInput');
      const radiusInput = document.getElementById('waypointRadiusInput');

      // Add keyboard shortcuts for the dialog
      const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          if (updateBtn) updateBtn.click();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.map.closePopup();
        }
      };

      // Add event listener to the popup container and all form inputs
      const popupContainer = document.querySelector('.waypoint-properties-popup');
      if (popupContainer) {
        popupContainer.addEventListener('keydown', handleKeyDown);
      }

      // Also add to individual inputs to ensure they capture the events
      [nameInput, idInput, typeSelect, latInput, lngInput, altInput, radiusInput].forEach(input => {
        if (input) {
          input.addEventListener('keydown', handleKeyDown);
        }
      });

      // Clean up event listeners when popup is closed
      const cleanupEventListeners = () => {
        if (popupContainer) {
          popupContainer.removeEventListener('keydown', handleKeyDown);
        }
        [nameInput, idInput, typeSelect, latInput, lngInput, altInput, radiusInput].forEach(input => {
          if (input) {
            input.removeEventListener('keydown', handleKeyDown);
          }
        });
        // Clear editing waypoint tracking
        this.currentEditingWaypointIndex = null;
      };

      // Store cleanup function for popup close event
      cleanupFn = cleanupEventListeners;

      // Listen for popup close event to cleanup properly
      popup.on('remove', () => {
        if (cleanupFn) {
          cleanupFn();
        }
      });

      if (deleteBtn) {
        deleteBtn.addEventListener('click', () => {
          cleanupEventListeners();
          this.map.closePopup();
          setTimeout(() => {
            this.deleteWaypoint(idx);
          }, 50);
        });
      }

      // Toggle route button event listener
      if (toggleRouteBtn) {
        toggleRouteBtn.addEventListener('click', (e) => {
          e.preventDefault(); // Prevent form submission
          e.stopPropagation(); // Prevent event from bubbling

          const wasInRoute = this.isWaypointInRoute(idx);
          const shouldBeInRoute = !wasInRoute;

          // Track which waypoint was toggled for highlighting
          this.lastToggledWaypointIndex = shouldBeInRoute ? idx : null;

          // Store which waypoints should be in route after this change
          const waypointsInRoute = new Set();
          for (let i = 0; i < this.waypoints.length; i++) {
            if (i === idx) {
              // This is the waypoint being changed
              if (shouldBeInRoute) {
                waypointsInRoute.add(i);
              }
            } else {
              // Other waypoints keep their current status
              if (this.isWaypointInRoute(i)) {
                waypointsInRoute.add(i);
              }
            }
          }

          // Remove all existing handles
          this.trackHandles.forEach(handle => this.map.removeLayer(handle));
          this.trackHandles = [];

          // Rebuild handles for waypoints that should be in route
          const validWaypointIndices = Array.from(waypointsInRoute).sort((a, b) => a - b);

          // Always keep track mode enabled once we start building a route
          // Track mode can only be disabled by explicitly clicking "Reset Task"
          this.trackMode = true;

          // Update UI button visibility based on number of waypoints
          const addTrackBtn = document.getElementById('addTrackBtn');
          const saveTask = document.getElementById('saveTask');
          const exportTask = document.getElementById('exportTask');

          if (validWaypointIndices.length > 0) {
            // Has waypoints in route - show save/export buttons
            if (addTrackBtn) addTrackBtn.style.display = 'none';
            if (saveTask && window.data.user_can_save_task) saveTask.style.display = 'block';
            if (exportTask) exportTask.style.display = 'block';
          } else {
            // No waypoints yet, but stay in track mode for building
            if (addTrackBtn) addTrackBtn.style.display = 'none';
            if (saveTask && window.data.user_can_save_task) saveTask.style.display = 'block';
            if (exportTask) exportTask.style.display = 'block';
          }

          // Create handles for route waypoints (minimum 1 required to start building route)
          if (validWaypointIndices.length >= 1) {
            validWaypointIndices.forEach((waypointIndex, handleIndex) => {
              if (waypointIndex < this.waypoints.length && this.markers[waypointIndex]) {
                const latlng = this.waypoints[waypointIndex];
                const isEndpoint = handleIndex === 0 || handleIndex === validWaypointIndices.length - 1;

                const handle = L.marker(latlng, {
                  icon: isEndpoint ? invisibleHandleIcon : handleIcon,
                  draggable: !isEndpoint
                }).addTo(this.map);

                handle.attachedWaypointIndex = waypointIndex;
                handle.autoInserted = false;

                if (!isEndpoint) {
                  this.attachHandleEvents(handle);
                }

                this.trackHandles.push(handle);
              }
            });
          }

          this.updateAutoHandles();
          this.updateRouteDrawing();
          this.showRouteWaypoints();
          // Close popup after toggling to avoid conflicts with other popups
          cleanupEventListeners();
          this.map.closePopup();
        });
      }

      updateBtn.addEventListener('click', async () => {

        const newName = nameInput.value.trim();

        // Always validate waypoint name (including blank, too short, and profanity)
        try {
          const response = await fetch(`${API_HOST}/v1/contest_task/validate_waypoint_name?name=${encodeURIComponent(newName)}`);
          const data = await response.json();

          if (!data.valid) {
            ToastNotification.make(data.error);
            nameInput.focus();
            return; // Block the update
          }
        } catch (error) {
          console.error('Error validating waypoint name:', error);
          ToastNotification.make(I18n.t("website.contest_task.editor.errors.failed_validate_waypoint"));
          return;
        }

        const newLabelIcon = L.divIcon({
          className: 'waypoint-label',
          html: `<div class="waypoint-text">${newName}</div>`,
          iconSize: [40, 20],
          iconAnchor: [20, 40]
        });
        this.markers[idx].label.setIcon(newLabelIcon);


        const newId = idInput.value.trim().toUpperCase();
        marker.waypointId = newId;


        const newType = typeSelect.value;
        marker.waypointType = newType;
        let newIcon;
        switch (newType) {
          case 'take_off':
            newIcon = startIcon;
            break;
          case 'goal':
            newIcon = endIcon;
            break;
          default:
            newIcon = waypointIcon;
        }
        marker.setIcon(newIcon);
        marker.waypointType = newType;


        const newLat = parseFloat(latInput.value);
        const newLng = parseFloat(lngInput.value);
        if (!isNaN(newLat) && !isNaN(newLng)) {
          const newLatLng = L.latLng(newLat, newLng);
          marker.setLatLng(newLatLng);
          this.markers[idx].label.setLatLng(newLatLng);
          this.markers[idx].circle.setLatLng(newLatLng);
          this.waypoints[idx] = newLatLng;

          // Fetch new altitude for the updated coordinates
          this.fetchAltitudeForWaypoint(idx, newLatLng, 100);
        }


        const newRadius = parseFloat(radiusInput.value);
        if (!isNaN(newRadius) && newRadius >= 0) {
          this.markers[idx].circle.setRadius(newRadius);
        }

        // Reset marker's draggable state to prevent duplication issues
        if (marker.dragging) {
          marker.dragging.disable();
          marker.dragging.enable();
        }

        cleanupEventListeners();
        this.map.closePopup();
        this.updateRouteDrawing();
        this.showRouteWaypoints();
        this.saveHistory();
      });

      // Note: No close button in popup HTML - Leaflet provides default close button
    }, 10);
  }

  blinkCircle(waypointIndex) {
    if (waypointIndex < 0 || waypointIndex >= this.markers.length) {
      return;
    }

    const circle = this.markers[waypointIndex].circle;
    if (!circle) {
      return;
    }

    // Store original style
    const originalFillOpacity = circle.options.fillOpacity;
    const originalColor = circle.options.color;
    const blinkColor = '#ffff00'; // Yellow for visibility
    const blinkOpacity = 0.8;
    const blinkDuration = 250; // milliseconds

    // Blink sequence: normal -> blink -> normal -> blink -> normal (2 complete blinks)
    let blinkCount = 0;
    const maxBlinks = 3

    const doBlink = () => {
      if (blinkCount >= maxBlinks) {
        // Restore original style
        circle.setStyle({
          fillOpacity: originalFillOpacity,
          color: originalColor
        });
        return;
      }

      // Blink on
      circle.setStyle({
        fillOpacity: blinkOpacity,
        color: blinkColor
      });

      setTimeout(() => {
        // Blink off
        circle.setStyle({
          fillOpacity: originalFillOpacity,
          color: originalColor
        });

        blinkCount++;

        // Continue blinking if we haven't reached max blinks
        if (blinkCount < maxBlinks) {
          setTimeout(doBlink, blinkDuration);
        }
      }, blinkDuration);
    };

    // Start the blink sequence
    doBlink();

    // Also pan to the waypoint on the map
    this.map.panTo(this.waypoints[waypointIndex]);
  }

  addWaypoint(latlng, type = 'waypoint', skipAltitudeFetch = false) {
    const index = this.waypoints.length;
    this.waypoints.push(latlng);
    
    
    if (type === 'start') {
      type = 'take_off';
    } else if (type === 'end') {
      type = 'goal';
    }
    
    let icon;
    switch (type) {
      case 'take_off':
        icon = startIcon;
        break;
      case 'goal':
        icon = endIcon;
        break;
      default:
        icon = waypointIcon;
    }
    
    const marker = L.marker(latlng, {
      icon: icon,
      draggable: true
    }).addTo(this.map);
    
    marker.waypointType = type;
    
    const labelText = type === 'take_off' ? 'Take Off' : 
                     type === 'goal' ? 'Goal' : 
                     `WP${index + 1}`;  
    
    const labelIcon = L.divIcon({
      className: 'waypoint-label',
      html: `<div class="waypoint-text">${labelText}</div>`,
      iconSize: [40, 20],
      iconAnchor: [20, 40]
    });
    const labelMarker = L.marker(latlng, {
      icon: labelIcon,
      interactive: false
    }).addTo(this.map);
    
    
    const circle = L.circle(latlng, { 
      radius: this.currentDefaultRadius,
      color: '#1579a3', 
      fill: true, 
      fillColor: '#1579a3', 
      fillOpacity: 0.3 
    }).addTo(this.map);
    
    this.markers.push({ marker, label: labelMarker, circle });

    marker.on('contextmenu', (e) => {
      e.originalEvent.preventDefault();
    });

    marker.on('click', () => {
      const idx = this.markers.findIndex(item => item.marker === marker);
      if (idx === -1) return;
      this.openWaypointEditor(idx);
    });

    // Store original position on drag start for snap-back effect
    marker.on('dragstart', (e) => {
      marker._originalLatLng = e.target.getLatLng();
    });

    marker.on('drag', (e) => {
      const newLatLng = e.target.getLatLng();
      labelMarker.setLatLng(newLatLng);
      if (circle) {
        circle.setLatLng(newLatLng);
      }
      const markerIndex = this.markers.findIndex(item => item.marker === marker);
      this.waypoints[markerIndex] = newLatLng;
      if (this.trackHandles && this.trackHandles.length) {
        this.trackHandles.forEach(handle => {
          if (handle.attachedWaypointIndex === markerIndex) {
            handle.setLatLng(newLatLng);
          }
        });
        this.updateMiddleHandlesNearWaypoint(markerIndex);
        this.updateRouteDrawing();
      }
    });
    marker.on('dragend', () => {
      const markerIndex = this.markers.findIndex(item => item.marker === marker);

      // If user is not the owner, snap back to original position with elastic effect
      if (!window.data?.user_can_save_task && marker._originalLatLng) {
        const originalLatLng = marker._originalLatLng;

        // Animate back to original position with elastic effect
        const duration = 400; // milliseconds
        const steps = 20;
        const stepDuration = duration / steps;
        let step = 0;

        const currentLatLng = marker.getLatLng();
        const latDiff = originalLatLng.lat - currentLatLng.lat;
        const lngDiff = originalLatLng.lng - currentLatLng.lng;

        const animate = () => {
          step++;
          // Easing function: elastic ease-out
          const t = step / steps;
          const easedT = t === 1 ? 1 : 1 - Math.pow(2, -10 * t) * Math.cos((t * 10 - 0.75) * (2 * Math.PI) / 3);

          const newLat = currentLatLng.lat + latDiff * easedT;
          const newLng = currentLatLng.lng + lngDiff * easedT;
          const newLatLng = L.latLng(newLat, newLng);

          marker.setLatLng(newLatLng);
          labelMarker.setLatLng(newLatLng);
          if (circle) {
            circle.setLatLng(newLatLng);
          }
          this.waypoints[markerIndex] = newLatLng;

          if (this.trackHandles && this.trackHandles.length) {
            this.trackHandles.forEach(handle => {
              if (handle.attachedWaypointIndex === markerIndex) {
                handle.setLatLng(newLatLng);
              }
            });
            this.updateRouteDrawing();
          }

          if (step < steps) {
            setTimeout(animate, stepDuration);
          } else {
            // Final position - ensure it's exact
            marker.setLatLng(originalLatLng);
            labelMarker.setLatLng(originalLatLng);
            if (circle) {
              circle.setLatLng(originalLatLng);
            }
            this.waypoints[markerIndex] = originalLatLng;
            this.updateRouteDrawing();

            // Show toast notification
            ToastNotification.make(I18n.t("website.contest_task.editor.errors.not_owner"));
          }
        };

        animate();
        return;
      }

      // Owner can edit - normal behavior
      if (markerIndex !== -1) {
        // Fetch altitude for the new position
        this.fetchAltitudeForWaypoint(markerIndex, marker.getLatLng(), 100);
      }
      this.showRouteWaypoints();
      this.saveHistory();
    });
    this.updateMenu();
    this.saveHistory();

    const waypointIndex = this.waypoints.length - 1;

    // Only fetch altitude for new waypoints, not when loading existing ones
    if (!skipAltitudeFetch) {
      this.fetchAltitudeForWaypoint(waypointIndex, latlng, 100);
    }

    return waypointIndex; 
  }

  deleteWaypoint(index) {
    const item = this.markers[index];
    this.map.removeLayer(item.marker);
    this.map.removeLayer(item.label);
    if (item.circle) {
      this.map.removeLayer(item.circle);
    }
    this.waypoints.splice(index, 1);
    this.markers.splice(index, 1);

    // Clear altitude data for this waypoint
    this.waypointAltitudes.delete(index);
    if (this.altitudeFetchTimeouts.has(index)) {
      clearTimeout(this.altitudeFetchTimeouts.get(index));
      this.altitudeFetchTimeouts.delete(index);
    }

    // Adjust altitude data indices for waypoints after the deleted one
    const newAltitudes = new Map();
    this.waypointAltitudes.forEach((altitude, wpIndex) => {
      if (wpIndex > index) {
        newAltitudes.set(wpIndex - 1, altitude);
      } else if (wpIndex < index) {
        newAltitudes.set(wpIndex, altitude);
      }
    });
    this.waypointAltitudes = newAltitudes;

    // Préserver les noms des waypoints existants
    this.markers.forEach((item, i) => {
      const currentLabelText = item.label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1];
      const newLabelIcon = L.divIcon({
        className: 'waypoint-label',
        html: `<div class="waypoint-text">${currentLabelText}</div>`,
        iconSize: [40, 20],
        iconAnchor: [20, 40]
      });
      item.label.setIcon(newLabelIcon);
    });

    if (this.trackMode && this.trackHandles.length) {
      // Build ordered list of waypoint indices in the route (excluding auto-inserted handles)
      const routeOrder = [];
      this.trackHandles.forEach(handle => {
        if (!handle.autoInserted && handle.attachedWaypointIndex !== null) {
          routeOrder.push(handle.attachedWaypointIndex);
        }
      });

      // Remove the deleted waypoint from the route order and adjust indices
      const newRouteOrder = routeOrder
        .filter(wpIndex => wpIndex !== index)
        .map(wpIndex => wpIndex > index ? wpIndex - 1 : wpIndex);

      // Remove all handles
      this.trackHandles.forEach(handle => this.map.removeLayer(handle));
      this.trackHandles = [];

      // Rebuild track handles in the original route order
      if (newRouteOrder.length >= 2) {
        newRouteOrder.forEach((waypointIndex, handleIndex) => {
          if (waypointIndex < this.waypoints.length && this.markers[waypointIndex]) {
            const latlng = this.waypoints[waypointIndex];
            const isEndpoint = handleIndex === 0 || handleIndex === newRouteOrder.length - 1;

            const handle = L.marker(latlng, {
              icon: isEndpoint ? invisibleHandleIcon : handleIcon,
              draggable: !isEndpoint
            }).addTo(this.map);

            handle.attachedWaypointIndex = waypointIndex;
            handle.autoInserted = false;

            if (!isEndpoint) {
              this.attachHandleEvents(handle);
            }

            this.trackHandles.push(handle);
          }
        });

        // Add auto-handles between waypoints
        this.updateAutoHandles();
      }

      this.updateRouteDrawing();
    }

    this.updateMenu();
    this.map.closePopup();
  }
 
  startTrackMode() {
    this.trackMode = true;
    if (this.trackHandles && this.trackHandles.length) {
      this.trackHandles.forEach(handle => this.map.removeLayer(handle));
    }
    this.renderer.clearTaskLayers();
    
    const center = this.map.getCenter();
    const offset = 0.15;
    const startLatLng = L.latLng(center.lat, center.lng - offset);
    const endLatLng = L.latLng(center.lat, center.lng + offset);
    
    const startWaypointIndex = this.addWaypoint(startLatLng, 'start');
    const endWaypointIndex = this.addWaypoint(endLatLng, 'end');

    const startHandle = L.marker(startLatLng, {
      icon: invisibleHandleIcon,
      draggable: false  
    }).addTo(this.map);
    startHandle.attachedWaypointIndex = startWaypointIndex;  
    startHandle.autoInserted = false;
    const endHandle = L.marker(endLatLng, {
      icon: invisibleHandleIcon,
      draggable: false  
    }).addTo(this.map);
    endHandle.attachedWaypointIndex = endWaypointIndex;  
    endHandle.autoInserted = false;
    this.trackHandles = [startHandle, endHandle];
    
    const midLatLng = L.latLng(
      (startLatLng.lat + endLatLng.lat) / 2,
      (startLatLng.lng + endLatLng.lng) / 2
    );
    const midHandle = L.marker(midLatLng, {
      icon: handleIcon,
      draggable: true
    }).addTo(this.map);
    midHandle.attachedWaypointIndex = null;
    midHandle.autoInserted = true;
    this.attachHandleEvents(midHandle);
    
    this.trackHandles = [startHandle, midHandle, endHandle];

    // Fit bounds to show the newly created route
    const bounds = L.latLngBounds([startLatLng, endLatLng]);
    this.map.fitBounds(bounds, {
      padding: [50, 50]
    });

    this.updateRouteDrawing();
    this.showRouteWaypoints();
  }

  handleDisconnection(handle) {
    
    const handleIndex = this.trackHandles.indexOf(handle);
    let prevHandle = null;
    let nextHandle = null;
    
    
    for (let i = handleIndex - 1; i >= 0; i--) {
      if (this.trackHandles[i].attachedWaypointIndex !== null) {
        prevHandle = this.trackHandles[i];
        break;
      }
    }
    
    
    for (let i = handleIndex + 1; i < this.trackHandles.length; i++) {
      if (this.trackHandles[i].attachedWaypointIndex !== null) {
        nextHandle = this.trackHandles[i];
        break;
      }
    }

    if (prevHandle && nextHandle) {
      
      const startIdx = this.trackHandles.indexOf(prevHandle);
      const endIdx = this.trackHandles.indexOf(nextHandle);
      const handlesBetween = this.trackHandles.slice(startIdx + 1, endIdx);
      handlesBetween.forEach(h => this.map.removeLayer(h));
      
      
      const midLatLng = L.latLng(
        (prevHandle.getLatLng().lat + nextHandle.getLatLng().lat) / 2,
        (prevHandle.getLatLng().lng + nextHandle.getLatLng().lng) / 2
      );
      const midHandle = L.marker(midLatLng, {
        icon: handleIcon,
        draggable: true
      }).addTo(this.map);
      midHandle.attachedWaypointIndex = null;
      midHandle.autoInserted = true;
      this.attachHandleEvents(midHandle);

      
      this.trackHandles = [
        ...this.trackHandles.slice(0, startIdx + 1),
        midHandle,
        ...this.trackHandles.slice(endIdx)
      ];
    }
  }

  attachHandleEvents(handle) {
    // Store original position on drag start for snap-back effect
    handle.on('dragstart', (e) => {
      handle._originalLatLng = e.target.getLatLng();
      handle._originalAttachedIndex = handle.attachedWaypointIndex;
      handle._originalAutoInserted = handle.autoInserted;
    });

    handle.on('dragend', () => {
      const pos = handle.getLatLng();

      // If user is not the owner, snap back with elastic effect and don't create waypoint
      if (!window.data?.user_can_save_task && handle._originalLatLng) {
        const originalLatLng = handle._originalLatLng;

        // Animate back to original position with elastic effect
        const duration = 400;
        const steps = 20;
        const stepDuration = duration / steps;
        let step = 0;

        const currentLatLng = handle.getLatLng();
        const latDiff = originalLatLng.lat - currentLatLng.lat;
        const lngDiff = originalLatLng.lng - currentLatLng.lng;

        const animate = () => {
          step++;
          const t = step / steps;
          const easedT = t === 1 ? 1 : 1 - Math.pow(2, -10 * t) * Math.cos((t * 10 - 0.75) * (2 * Math.PI) / 3);

          const newLat = currentLatLng.lat + latDiff * easedT;
          const newLng = currentLatLng.lng + lngDiff * easedT;
          const newLatLng = L.latLng(newLat, newLng);

          handle.setLatLng(newLatLng);
          this.updateRouteDrawing();

          if (step < steps) {
            setTimeout(animate, stepDuration);
          } else {
            // Final position - restore original state
            handle.setLatLng(originalLatLng);
            handle.attachedWaypointIndex = handle._originalAttachedIndex;
            handle.autoInserted = handle._originalAutoInserted;
            this.updateRouteDrawing();

            // Show toast notification
            ToastNotification.make(I18n.t("website.contest_task.editor.errors.not_owner"));
          }
        };

        animate();
        return;
      }

      // Owner can edit - normal behavior
      let snapped = false;
      const wasAttached = handle.attachedWaypointIndex !== null;
      const wasAutoInserted = handle.autoInserted;

      const isEndpoint = this.trackHandles.indexOf(handle) === 0 ||
                        this.trackHandles.indexOf(handle) === this.trackHandles.length - 1;

      if (isEndpoint && wasAttached) {
        handle.setLatLng(this.waypoints[handle.attachedWaypointIndex]);
        return;
      }

      for (let i = 0; i < this.waypoints.length; i++) {
        const wp = this.waypoints[i];
        if (pos.distanceTo(wp) < 800) {
          const isWaypointTaken = this.trackHandles.some(h =>
            h !== handle && h.attachedWaypointIndex === i && !h.autoInserted
          );

          if (!isWaypointTaken) {
            handle.setLatLng(wp);
            handle.attachedWaypointIndex = i;
            handle.autoInserted = false;
            snapped = true;
            break;
          }
        }
      }

      if (!snapped && !isEndpoint) {
        if (wasAttached) {
          handle.attachedWaypointIndex = null;
        }

        const newWaypointIndex = this.addWaypoint(pos);
        handle.attachedWaypointIndex = newWaypointIndex;
        handle.autoInserted = false;
        handle.setLatLng(this.waypoints[newWaypointIndex]);
      }

      this.updateAutoHandles();
      this.updateRouteDrawing();

      // Update the waypoint list if we converted an auto-inserted handle to a waypoint
      if (wasAutoInserted && handle.attachedWaypointIndex !== null) {
        this.showRouteWaypoints();
      }
    });

    handle.on('drag', () => {
      this.updateRouteDrawing();
    });
  }

  updateAutoHandles() {
   
    let currentHandles = this.trackHandles.slice();
    let newHandles = [];
    for (let i = 0; i < currentHandles.length - 1; i++) {
      newHandles.push(currentHandles[i]);
      let A = currentHandles[i];
      let B = currentHandles[i + 1];
      if (A.attachedWaypointIndex != null && B.attachedWaypointIndex != null) {
        let distance = A.getLatLng().distanceTo(B.getLatLng());
        if (distance > 800) {  
          let midLatLng = L.latLng(
            (A.getLatLng().lat + B.getLatLng().lat) / 2,
            (A.getLatLng().lng + B.getLatLng().lng) / 2
          );
          let autoHandle = L.marker(midLatLng, {
            icon: handleIcon,
            draggable: true
          }).addTo(this.map);
          autoHandle.attachedWaypointIndex = null;
          autoHandle.autoInserted = true;
          this.attachHandleEvents(autoHandle);
          newHandles.push(autoHandle);
        }
      }
    }
    if (currentHandles.length > 0) {
      newHandles.push(currentHandles[currentHandles.length - 1]);
    }
    this.trackHandles = newHandles;
  }


  updateRouteDrawing() {
    console.log("updateRouteDrawing: Updating track from handles");

    // Use the renderer to display the route
    this.renderer.clearTaskLayers();
    this.renderer.renderFromWaypointsAndMarkers(this.waypoints, this.markers, this.trackHandles);

    this.updateTrackInfo();
  }

  finishTrackMode() {
    this.trackMode = false;
    if (this.trackHandles && this.trackHandles.length) {
      this.trackHandles.forEach(handle => this.map.removeLayer(handle));
      this.trackHandles = [];
    }
    this.renderer.clearTaskLayers();
    document.getElementById('addTrackBtn').style.display = 'block';
    this.updateTrackInfo();
    this.showRouteWaypoints();
  }

 
  computeTotalWaypointDistance() {
    let total = 0;
    for (let i = 1; i < this.waypoints.length; i++) {
      total += this.waypoints[i - 1].distanceTo(this.waypoints[i]);
    }
    return total.toFixed(2);
  }

  computeTrackDistance() {
    // Calculate distance from center to center of waypoints only (not including auto-inserted handles)
    let total = 0;
    if (this.trackHandles && this.trackHandles.length > 1) {
      const waypointHandles = this.trackHandles.filter(h => !h.autoInserted && h.attachedWaypointIndex !== null);
      for (let i = 1; i < waypointHandles.length; i++) {
        total += waypointHandles[i - 1].getLatLng().distanceTo(waypointHandles[i].getLatLng());
      }
    }
    if (total >= 2000) {
      return `${(total / 1000).toFixed(1)} km`;
    } else {
      return `${total.toFixed(1)} m`;
    }
  }

  computeOptimizedDistance() {
    // Calculate the optimized path distance (red dashed line with tangent points)
    if (!this.trackHandles || this.trackHandles.length < 2) {
      return `0.0 m`;
    }

    // Get ordered waypoints (same logic as renderer)
    const orderedWaypoints = [];
    this.trackHandles.forEach((handle) => {
      if (handle.attachedWaypointIndex !== null &&
          handle.attachedWaypointIndex < this.waypoints.length &&
          this.markers[handle.attachedWaypointIndex]) {
        orderedWaypoints.push({
          point: this.waypoints[handle.attachedWaypointIndex],
          radius: this.markers[handle.attachedWaypointIndex].circle?.getRadius() || 0
        });
      }
    });

    if (orderedWaypoints.length < 2) {
      return `0.0 m`;
    }

    const pathPoints = [];

    // First point (center of first waypoint)
    pathPoints.push(orderedWaypoints[0].point);

    // Intermediate points (tangent to circles)
    for (let i = 1; i < orderedWaypoints.length - 1; i++) {
      const prev = orderedWaypoints[i - 1];
      const curr = orderedWaypoints[i];
      const next = orderedWaypoints[i + 1];

      if (curr.radius === 0) {
        pathPoints.push(curr.point);
        continue;
      }

      const optimalPoint = this.findOptimalPointOnCircle(
        prev.point,
        curr.point,
        next.point,
        curr.radius
      );
      pathPoints.push(optimalPoint);
    }

    // Last point (center of last waypoint)
    pathPoints.push(orderedWaypoints[orderedWaypoints.length - 1].point);

    // Calculate total distance along the optimized path
    let total = 0;
    for (let i = 1; i < pathPoints.length; i++) {
      total += pathPoints[i - 1].distanceTo(pathPoints[i]);
    }

    if (total >= 2000) {
      return `${(total / 1000).toFixed(1)} km`;
    } else {
      return `${total.toFixed(1)} m`;
    }
  }

  findOptimalPointOnCircle(prevPoint, circleCenter, nextPoint, radius) {
    const scale = Math.cos(circleCenter.lat * Math.PI / 180) * 111111;
    const x0 = (prevPoint.lng - circleCenter.lng) * scale;
    const y0 = (prevPoint.lat - circleCenter.lat) * 111111;
    const x1 = (nextPoint.lng - circleCenter.lng) * scale;
    const y1 = (nextPoint.lat - circleCenter.lat) * 111111;

    const anglePrev = Math.atan2(y0, x0);
    const angleNext = Math.atan2(y1, x1);

    let optimalAngle = (anglePrev + angleNext) / 2;
    if (Math.abs(angleNext - anglePrev) > Math.PI) {
      optimalAngle += Math.PI;
    }

    return L.latLng(
      circleCenter.lat + (Math.sin(optimalAngle) * radius) / 111111,
      circleCenter.lng + (Math.cos(optimalAngle) * radius) / scale
    );
  }

  updateTrackInfo() {
    const trackInfoDiv = document.getElementById('track-info');
    const permanentChallengeInfo = document.getElementById('permanent-challenge-info');

    if (this.trackMode) {
      let trackDistance = this.computeTrackDistance();
      let optimizedDistance = this.computeOptimizedDistance();

      // Count unique waypoints in the route (trackHandles order)
      // Count all waypoints that are part of the route, regardless of type
      const waypointIndicesInRoute = new Set();
      this.trackHandles.forEach(h => {
        if (h.attachedWaypointIndex !== null) {
          waypointIndicesInRoute.add(h.attachedWaypointIndex);
        }
      });
      const numWaypoints = waypointIndicesInRoute.size;
      const numSegments = Math.max(0, numWaypoints - 1);

      trackInfoDiv.innerHTML = `
        ${I18n.t("website.contest_task.editor.info.open_distance")}: ${trackDistance} (${numSegments} ${I18n.t("website.contest_task.editor.info.segments")}, ${numWaypoints} ${I18n.t("website.contest_task.editor.info.waypoints")})<br>
        ${I18n.t("website.contest_task.editor.info.optimized_distance")}: ${optimizedDistance}`;

      // Show/hide permanent challenge warning based on dateFieldsEnabled
      if (permanentChallengeInfo) {
        if (this.dateFieldsEnabled) {
          permanentChallengeInfo.classList.remove('d-none');
        } else {
          permanentChallengeInfo.classList.add('d-none');
        }
      }
    } else {
      trackInfoDiv.innerHTML = `${I18n.t("website.contest_task.editor.info.open_distance")}: --`;
      // Hide warning when not in track mode
      if (permanentChallengeInfo) {
        permanentChallengeInfo.classList.add('d-none');
      }
    }
  }
 
  initMenu() {
    const addTrackBtn = document.getElementById('addTrackBtn');
    const saveTaskBtn = document.getElementById('saveTask');
    const exportTaskBtn = document.getElementById('exportTask');
    const importTaskBtn = document.getElementById('importTask');
    const setDateBtn = document.getElementById('setDate');
    const importFileInput = document.createElement('input');
    importFileInput.type = 'file';
    importFileInput.accept = '.xctsk,.wpt,.cup,.png';
    importFileInput.style.display = 'none';
    document.body.appendChild(importFileInput);

    // Track whether date fields are enabled
    this.dateFieldsEnabled = false;

    const resetTaskBtn = document.getElementById('resetTask');
    if (resetTaskBtn) {
      resetTaskBtn.addEventListener('click', () => {
        if (window.confirm(I18n.t("website.contest_task.editor.buttons.reset_confirm"))) {
          this.resetTask();
        }
      });
    }

    addTrackBtn.addEventListener('click', () => {
      this.startTrackMode();
      addTrackBtn.style.display = 'none';
      if (saveTaskBtn) {
        saveTaskBtn.style.display = 'block';
      }
      exportTaskBtn.style.display = 'block';
      if (setDateBtn) {
        setDateBtn.style.display = 'inline-block';
      }
      this.updateMenu();
    });

    if (setDateBtn) {
      setDateBtn.addEventListener('click', () => {
        this.dateFieldsEnabled = !this.dateFieldsEnabled;
        this.toggleDateFields();
        this.updateTrackInfo();
      });
    }

    importTaskBtn.addEventListener('click', () => {
      importFileInput.click();
    });

    importFileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const ext = file.name.split('.').pop().toLowerCase();

      // Handle PNG files (QR codes) separately
      if (ext === 'png') {
        this.importQRCode(file).catch(err => {
          console.error('Error importing QR code:', err);
        });
        return;
      }

      // Handle text-based files
      const reader = new FileReader();

      reader.onload = (evt) => {
        const content = evt.target.result;
        if (ext === 'xctsk') {
          try {
            const taskData = JSON.parse(content);
            this.importXCTSK(taskData);
          } catch (err) {
            ToastNotification.make(I18n.t("website.contest_task.editor.errors.invalid_xctsk"));
          }
        } else if (ext === 'wpt') {
          try {
            const waypoints = this.parseWPT(content);
            this.importWPT(waypoints);
          } catch (err) {
            ToastNotification.make(I18n.t("website.contest_task.editor.errors.invalid_wpt"));
          }
        } else if (ext === 'cup') {
          try {
            this.importCUP(content);
          } catch (err) {
            ToastNotification.make(I18n.t("website.contest_task.editor.errors.invalid_cup"));
          }
        } else {
          ToastNotification.make(I18n.t("website.contest_task.editor.errors.unsupported_file"));
        }
      };

      reader.readAsText(file);
    });

    if (saveTaskBtn) {
      saveTaskBtn.addEventListener('click', () => {
        this.saveContestTask();
      });
    }

    exportTaskBtn.addEventListener('click', () => {
      this.showExportDialog();
    });

    // Duplicate button handler
    const duplicateTaskBtn = document.getElementById('duplicateTask');
    if (duplicateTaskBtn) {
      duplicateTaskBtn.addEventListener('click', () => {
        this.duplicateTask();
      });
    }

    if (saveTaskBtn) {
      saveTaskBtn.style.display = 'none';
    }
    exportTaskBtn.style.display = 'none';

    // Initially hide setDate button unless in edit mode
    if (setDateBtn) {
      if (this.isEditMode) {
        setDateBtn.style.display = 'inline-block';
      } else {
        setDateBtn.style.display = 'none';
      }
    }

    this.showRouteWaypoints();
  }

  parseWPT(content) {
    // Retourne un tableau d'objets { name, lat, lon, alt }
    const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const waypoints = [];

    // Detect format based on content
    const firstLines = lines.slice(0, 5).join('\n');
    let format = 'unknown';

    if (firstLines.includes('OziExplorer Waypoint File')) {
      format = 'ozi';
    } else if (firstLines.includes('$FormatGEO')) {
      format = 'gpsdump';
    } else if (firstLines.includes('G  WGS 84') || lines.some(line => line.startsWith('W ') && line.includes('N ') && line.includes('E '))) {
      format = 'compegps';
    }

    for (const line of lines) {
      try {
        let waypoint = null;

        if (format === 'ozi') {
          // OziExplorer format: number,name,lat,lon,,,1,,,,,,,elevation
          if (line.includes(',') && !line.startsWith('OziExplorer') && !line.startsWith('WGS') && !line.startsWith('Reserved')) {
            const parts = line.split(',');
            if (parts.length >= 4) {
              const name = parts[1].trim();
              const lat = parseFloat(parts[2]);
              const lon = parseFloat(parts[3]);
              const alt = parseInt(parts[14], 10) || 0;
              if (!isNaN(lat) && !isNaN(lon) && name) {
                waypoint = { name, lat, lon, alt };
              }
            }
          }
        } else if (format === 'compegps') {
          // CompeGPS format: W name A lat[N/S] lon[E/W] date time elevation description
          if (line.startsWith('W ')) {
            const parts = line.split(/\s+/);
            if (parts.length >= 7) {
              const name = parts[1];
              const latStr = parts[3]; // e.g., "46.55148315N"
              const lonStr = parts[4]; // e.g., "14.76980019E"

              // Parse latitude
              const latMatch = latStr.match(/([\d\.\-]+)([NS])/);
              const lonMatch = lonStr.match(/([\d\.\-]+)([EW])/);

              if (latMatch && lonMatch) {
                let lat = parseFloat(latMatch[1]);
                let lon = parseFloat(lonMatch[1]);
                if (latMatch[2] === 'S') lat = -lat;
                if (lonMatch[2] === 'W') lon = -lon;
                const alt = parseInt(parts[6], 10) || 0;
                waypoint = { name, lat, lon, alt };
              }
            }
          }
        } else if (format === 'gpsdump') {
          // GPSDump format: name [N/S] DD MM SS.SS [E/W] DDD MM SS.SS elevation
          if (line !== '$FormatGEO' && line.includes(' ')) {
            const parts = line.split(/\s+/);
            if (parts.length >= 7) {
              const name = parts[0];
              const latHem = parts[1]; // N or S
              const latDeg = parseInt(parts[2]);
              const latMin = parseInt(parts[3]);
              const latSec = parseFloat(parts[4]);
              const lonHem = parts[5]; // E or W
              const lonDeg = parseInt(parts[6]);
              const lonMin = parseInt(parts[7]);
              const lonSec = parseFloat(parts[8]);
              const alt = parseInt(parts[9], 10) || 0;

              // Convert DMS to decimal degrees
              let lat = latDeg + latMin / 60 + latSec / 3600;
              let lon = lonDeg + lonMin / 60 + lonSec / 3600;

              if (latHem === 'S') lat = -lat;
              if (lonHem === 'W') lon = -lon;

              if (!isNaN(lat) && !isNaN(lon) && name) {
                waypoint = { name, lat, lon, alt };
              }
            }
          }
        }

        if (waypoint) {
          waypoints.push(waypoint);
        }
      } catch (e) {
        // Skip lines that can't be parsed
        continue;
      }
    }

    return waypoints;
  }

  importWPT(waypoints) {
    if (this.trackHandles) {
      this.trackHandles.forEach(handle => this.map.removeLayer(handle));
      this.trackHandles = [];
    }

    while (this.waypoints.length > 0) {
      this.deleteWaypoint(0);
    }

    if (this.wptClusterGroup) {
      this.map.removeLayer(this.wptClusterGroup);
    }

    // Import as markers - no task definition
    this.trackMode = false;

    const bounds = L.latLngBounds([]);

    waypoints.forEach((wp) => {
      const latlng = L.latLng(wp.lat, wp.lon);
      bounds.extend(latlng);

      const wpIndex = this.addWaypoint(latlng, 'waypoint');
      const marker = this.markers[wpIndex];

      const newLabelIcon = L.divIcon({
        className: 'waypoint-label',
        html: `<div class="waypoint-text">${wp.name}</div>`,
        iconSize: [40, 20],
        iconAnchor: [20, 40]
      });
      marker.label.setIcon(newLabelIcon);

      if (marker.circle) {
        marker.circle.setRadius(400);
      }
    });

    this.updateAutoHandles();

    this.map.fitBounds(bounds, {
      padding: [20, 20]
    });

    // Waypoints imported - show add track button and export, hide save
    document.getElementById('addTrackBtn').style.display = 'block';
    const saveTask = document.getElementById('saveTask');
    if (saveTask) {
      saveTask.style.display = 'none';
    }
    document.getElementById('exportTask').style.display = 'block';

    this.updateRouteDrawing();
    this.showRouteWaypoints();
    this.updateMenu();
  }

  async importQRCode(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          // Create canvas to extract image data
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          context.drawImage(img, 0, 0);

          // Get image data
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

          // Decode QR code
          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code) {
            try {
              // QR code data should start with "XCTSK:"
              let qrData = code.data;

              if (qrData.startsWith('XCTSK:')) {
                // Remove the prefix and parse JSON
                const jsonData = qrData.substring(6);
                const taskData = JSON.parse(jsonData);

                // Import the task data
                this.importXCTSK(taskData);
                ToastNotification.make(I18n.t("website.contest_task.editor.success.qr_code_imported"));
                resolve(taskData);
              } else {
                ToastNotification.make(I18n.t("website.contest_task.editor.errors.invalid_qr_code_format"));
                reject(new Error('Invalid QR code format'));
              }
            } catch (error) {
              console.error('Error parsing QR code data:', error);
              ToastNotification.make(I18n.t("website.contest_task.editor.errors.invalid_qr_code_data"));
              reject(error);
            }
          } else {
            ToastNotification.make(I18n.t("website.contest_task.editor.errors.no_qr_code_found"));
            reject(new Error('No QR code found in image'));
          }
        };

        img.onerror = () => {
          ToastNotification.make(I18n.t("website.contest_task.editor.errors.failed_load_image"));
          reject(new Error('Failed to load image'));
        };

        img.src = e.target.result;
      };

      reader.onerror = () => {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.failed_read_file"));
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  // Decode polyline-encoded coordinates (Format 2)
  // XCTrack uses: longitude, latitude, altitude, radius (lon first!)
  decodePolyline(encoded) {
    const points = [];
    let index = 0;
    let lat = 0;
    let lng = 0;
    let alt = 0;
    let radius = 0;

    while (index < encoded.length) {
      let shift = 0;
      let result = 0;
      let byte;

      // Decode LONGITUDE first (XCTrack uses lon,lat order in polyline)
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);
      const deltaLng = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lng += deltaLng;

      shift = 0;
      result = 0;

      // Decode LATITUDE second
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);
      const deltaLat = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lat += deltaLat;

      // Decode altitude (if present)
      if (index < encoded.length && encoded.charCodeAt(index) !== 63) {
        shift = 0;
        result = 0;
        do {
          byte = encoded.charCodeAt(index++) - 63;
          result |= (byte & 0x1f) << shift;
          shift += 5;
        } while (byte >= 0x20);
        const deltaAlt = ((result & 1) ? ~(result >> 1) : (result >> 1));
        alt += deltaAlt;
      }

      // Decode radius (if present)
      if (index < encoded.length && encoded.charCodeAt(index) !== 63) {
        shift = 0;
        result = 0;
        do {
          byte = encoded.charCodeAt(index++) - 63;
          result |= (byte & 0x1f) << shift;
          shift += 5;
        } while (byte >= 0x20);
        const deltaRadius = ((result & 1) ? ~(result >> 1) : (result >> 1));
        radius += deltaRadius;
      }

      points.push({
        lat: lat / 1e5,
        lon: lng / 1e5,
        alt: alt / 10,
        radius: radius
      });
    }

    return points[0]; // Return first point (each waypoint has one encoded point)
  }

  importXCTSK(taskData) {
    console.log('importXCTSK called with:', taskData);
    console.log('Version:', taskData.V || taskData.version);
    console.log('All properties:', Object.keys(taskData));

    // Format 2 uses different property names (minified)
    // Format 1: taskData.turnpoints
    // Format 2: taskData.t (and V=2)
    let turnpoints = taskData.turnpoints;
    let isFormat2 = false;

    // Check for Format 2 structure (minified property names)
    if (!turnpoints && (taskData.V === 2 || taskData.version === 2)) {
      console.log('Detected Format 2 with minified properties');
      isFormat2 = true;
      if (taskData.t && Array.isArray(taskData.t)) {
        // Convert Format 2 to Format 1 structure
        turnpoints = taskData.t.map(tp => {
          const decoded = this.decodePolyline(tp.z);
          console.log('Decoded waypoint:', tp.n, decoded);
          return {
            waypoint: {
              lat: decoded.lat,
              lon: decoded.lon,
              altSmoothed: decoded.alt,
              name: tp.n
            },
            radius: decoded.radius || 400,
            type: tp.t || 'WAYPOINT' // Default type if not specified
          };
        });
      }
    }

    console.log('Using turnpoints:', turnpoints);

    if (!turnpoints || !Array.isArray(turnpoints)) {
      console.error('Invalid turnpoints:', turnpoints);
      console.error('Full taskData:', JSON.stringify(taskData, null, 2));
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.invalid_xctsk_missing_turnpoints"));
      return;
    }

    // Store for use in the rest of the function
    taskData.turnpoints = turnpoints;

    if (this.trackHandles) {
      this.trackHandles.forEach(handle => this.map.removeLayer(handle));
      this.trackHandles = [];
    }

    while (this.waypoints.length > 0) {
      this.deleteWaypoint(0);
    }

    this.trackMode = true;

    const bounds = L.latLngBounds([]);
    const newHandles = [];

    // Helper function to parse coordinates from both Format 1 and Format 2
    const parseWaypointCoordinates = (tp) => {
      console.log('Parsing turnpoint:', tp);
      console.log('tp.waypoint type:', typeof tp.waypoint);

      // Format 2: coordinates are stored as a string "lat,lon,alt,radius"
      if (typeof tp.waypoint === 'string') {
        const parts = tp.waypoint.split(',');
        const result = {
          lat: parseFloat(parts[0]),
          lon: parseFloat(parts[1]),
          alt: parseFloat(parts[2]) || 1000,
          radius: tp.radius || parseInt(parts[3]) || 400,
          name: tp.name || 'WP'
        };
        console.log('Format 2 parsed result:', result);
        return result;
      }
      // Format 1: coordinates are nested in waypoint object
      else if (tp.waypoint && typeof tp.waypoint === 'object') {
        const result = {
          lat: tp.waypoint.lat,
          lon: tp.waypoint.lon,
          alt: tp.waypoint.altSmoothed || tp.waypoint.alt || 1000,
          radius: tp.radius || 400,
          name: tp.waypoint.name || tp.name || 'WP'
        };
        console.log('Format 1 parsed result:', result);
        return result;
      }
      console.warn('Could not parse waypoint coordinates:', tp);
      return null;
    };

    const connectedTurnpoints = taskData.turnpoints.filter(tp => tp.type !== 'MARK');
    const unconnectedTurnpoints = taskData.turnpoints.filter(tp => tp.type === 'MARK');

    console.log('Connected turnpoints:', connectedTurnpoints.length);
    console.log('Unconnected turnpoints:', unconnectedTurnpoints.length);

    connectedTurnpoints.forEach((tp, index) => {
      console.log(`Processing connected turnpoint ${index}:`, tp);
      const coords = parseWaypointCoordinates(tp);
      if (!coords) return;

      const latlng = L.latLng(coords.lat, coords.lon);
      bounds.extend(latlng);
      let type;
      
      switch (tp.type) {
        case 'TAKEOFF':
          type = 'take_off';
          break;
        case 'SSS':
          type = 'sss';
          break;
        case 'ESS':
          type = 'ess';
          break;
        case 'GOAL':
          type = 'goal';
          break;
        default:
          type = 'waypoint';
      }

      const wpIndex = this.addWaypoint(latlng, type, true); // Skip altitude fetch, we have it
      const marker = this.markers[wpIndex];

      // Store altitude from the task data
      this.waypointAltitudes.set(wpIndex, coords.alt);

      const newLabelIcon = L.divIcon({
        className: 'waypoint-label',
        html: `<div class="waypoint-text">${coords.name}</div>`,
        iconSize: [40, 20],
        iconAnchor: [20, 40]
      });
      marker.label.setIcon(newLabelIcon);
      if (marker.circle) {
        marker.circle.setRadius(coords.radius);
      }
      
      const handle = L.marker(latlng, {
        icon: index === 0 || index === connectedTurnpoints.length - 1 ? invisibleHandleIcon : handleIcon,
        draggable: !(index === 0 || index === connectedTurnpoints.length - 1)
      }).addTo(this.map);
      handle.attachedWaypointIndex = wpIndex;
      handle.autoInserted = false;
      if (!(index === 0 || index === connectedTurnpoints.length - 1)) {
        this.attachHandleEvents(handle);
      }
      newHandles.push(handle);
    });

    unconnectedTurnpoints.forEach(tp => {
      const coords = parseWaypointCoordinates(tp);
      if (!coords) return;

      const latlng = L.latLng(coords.lat, coords.lon);
      bounds.extend(latlng);

      const wpIndex = this.addWaypoint(latlng, 'mark', true); // Skip altitude fetch, we have it
      const marker = this.markers[wpIndex];

      // Store altitude from the task data
      this.waypointAltitudes.set(wpIndex, coords.alt);

      const newLabelIcon = L.divIcon({
        className: 'waypoint-label',
        html: `<div class="waypoint-text">${coords.name}</div>`,
        iconSize: [40, 20],
        iconAnchor: [20, 40]
      });
      marker.label.setIcon(newLabelIcon);
      if (marker.circle) {
        marker.circle.setRadius(coords.radius);
      }

    });

    this.trackHandles = newHandles;
    this.updateAutoHandles();

    this.map.fitBounds(bounds, {
      padding: [20, 20]
    });

    if (taskData.takeoff) {
      if (taskData.takeoff.timeOpen) {
        document.getElementById('startTimeBeginsInput').value = 
          taskData.takeoff.timeOpen.split('.')[0];
      }
      if (taskData.takeoff.timeClose) {
        document.getElementById('startTimeEndsInput').value = 
          taskData.takeoff.timeClose.split('.')[0];
      }
    }
    
    if (taskData.goal?.deadline) {
      document.getElementById('goalDeadlineTimeInput').value = 
        taskData.goal.deadline.split('.')[0];
    }

    document.getElementById('addTrackBtn').style.display = 'none';
    const saveTask = document.getElementById('saveTask');
    if (saveTask) {
      document.getElementById('saveTask').style.display = 'block';
    }
    document.getElementById('exportTask').style.display = 'block';

    if (taskData.sss && Array.isArray(taskData.sss.timeGates) && taskData.sss.timeGates.length > 0) {
      const startTime = taskData.sss.timeGates[0];
      const startInput = document.getElementById('startTimeBeginsInput');
      if (startInput) startInput.value = startTime.replace('Z', '');
    }
    
    if (taskData.goal && taskData.goal.deadline) {
      let deadline = taskData.goal.deadline;
      if (Array.isArray(deadline)) {
        deadline = deadline[0];
      }
      deadline = deadline.replace('Z', '');
      const endInput = document.getElementById('startTimeEndsInput');
      const goalInput = document.getElementById('goalDeadlineTimeInput');
      if (endInput) endInput.value = deadline;
      if (goalInput) goalInput.value = deadline;
    }

    this.updateRouteDrawing();
    this.showRouteWaypoints();
    this.updateMenu();
  }

  importCUP(content) {
    if (!content || typeof content !== 'string') {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.invalid_cup_content"));
      return;
    }

    try {
      const sections = content.split('-----Related Tasks-----');
      const waypointSection = sections[0];
      const taskSection = sections[1];

      if (!waypointSection) {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.invalid_cup_missing_waypoints"));
        return;
      }

      const waypoints = this.parseCUPWaypoints(waypointSection);

      if (waypoints.length === 0) {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.no_waypoints_found"));
        return;
      }

      if (this.trackHandles) {
        this.trackHandles.forEach(handle => this.map.removeLayer(handle));
        this.trackHandles = [];
      }

      while (this.waypoints.length > 0) {
        this.deleteWaypoint(0);
      }

      if (this.wptClusterGroup) {
        this.map.removeLayer(this.wptClusterGroup);
      }

      const bounds = L.latLngBounds([]);

      // Check if file contains a task definition
      const hasTask = taskSection && taskSection.trim().length > 0;

      if (hasTask) {
        // Import as route - has task definition
        this.trackMode = true;
        const newHandles = [];

        // Parse task definition to get route order
        const taskLines = taskSection.trim().split('\n');
        const taskLine = taskLines.find(line => line.startsWith('"null"'));

        if (taskLine) {
          // Extract waypoint names from task line
          const taskWaypointNames = taskLine.split(',')
            .slice(2) // Skip "null" and empty field
            .map(name => name.replace(/"/g, '').trim())
            .filter(name => name && name !== '');

          // Create waypoints in task order
          taskWaypointNames.forEach((wpName, index) => {
            const wp = waypoints.find(w => w.name === wpName);
            if (!wp) return;

            const latlng = L.latLng(wp.lat, wp.lon);
            bounds.extend(latlng);

            let type = 'waypoint';
            if (index === 0) {
              type = 'take_off';
            } else if (index === taskWaypointNames.length - 1) {
              type = 'goal';
            }

            const wpIndex = this.addWaypoint(latlng, type);
            const marker = this.markers[wpIndex];

            const newLabelIcon = L.divIcon({
              className: 'waypoint-label',
              html: `<div class="waypoint-text">${wp.name}</div>`,
              iconSize: [40, 20],
              iconAnchor: [20, 40]
            });
            marker.label.setIcon(newLabelIcon);

            if (marker.circle) {
              marker.circle.setRadius(400);
            }

            const handle = L.marker(latlng, {
              icon: index === 0 || index === taskWaypointNames.length - 1 ? invisibleHandleIcon : handleIcon,
              draggable: !(index === 0 || index === taskWaypointNames.length - 1)
            }).addTo(this.map);
            handle.attachedWaypointIndex = wpIndex;
            handle.autoInserted = false;

            if (!(index === 0 || index === taskWaypointNames.length - 1)) {
              this.attachHandleEvents(handle);
            }
            newHandles.push(handle);
          });

          // Add any remaining waypoints as markers (not part of route)
          waypoints.forEach(wp => {
            const isInRoute = taskWaypointNames.includes(wp.name);
            if (!isInRoute) {
              const latlng = L.latLng(wp.lat, wp.lon);
              bounds.extend(latlng);

              const wpIndex = this.addWaypoint(latlng, 'waypoint');
              const marker = this.markers[wpIndex];

              const newLabelIcon = L.divIcon({
                className: 'waypoint-label',
                html: `<div class="waypoint-text">${wp.name}</div>`,
                iconSize: [40, 20],
                iconAnchor: [20, 40]
              });
              marker.label.setIcon(newLabelIcon);

              if (marker.circle) {
                marker.circle.setRadius(400);
              }
            }
          });

          this.trackHandles = newHandles;
        }
      } else {
        // Import as markers - no task definition
        this.trackMode = false;

        waypoints.forEach((wp) => {
          const latlng = L.latLng(wp.lat, wp.lon);
          bounds.extend(latlng);

          const wpIndex = this.addWaypoint(latlng, 'waypoint');
          const marker = this.markers[wpIndex];

          const newLabelIcon = L.divIcon({
            className: 'waypoint-label',
            html: `<div class="waypoint-text">${wp.name}</div>`,
            iconSize: [40, 20],
            iconAnchor: [20, 40]
          });
          marker.label.setIcon(newLabelIcon);

          if (marker.circle) {
            marker.circle.setRadius(400);
          }
        });
      }
      this.updateAutoHandles();
      
      this.map.fitBounds(bounds, {
        padding: [20, 20]
      });
      
      // Set button visibility and update display based on whether we imported a task or just waypoints
      if (hasTask) {
        // Task imported - show save/export buttons, update route
        document.getElementById('addTrackBtn').style.display = 'none';
        const saveTask = document.getElementById('saveTask');
        if (saveTask) {
          saveTask.style.display = 'block';
        }
        document.getElementById('exportTask').style.display = 'block';

        this.updateAutoHandles();
        this.updateRouteDrawing();
        ToastNotification.make(I18n.t("website.contest_task.editor.success.task_imported_cup"));
      } else {
        // Only waypoints imported - show add track button and export
        document.getElementById('addTrackBtn').style.display = 'block';
        const saveTask = document.getElementById('saveTask');
        if (saveTask) {
          saveTask.style.display = 'none';
        }
        document.getElementById('exportTask').style.display = 'block';

        this.updateTrackInfo();
        ToastNotification.make(I18n.t("website.contest_task.editor.success.waypoints_imported_cup", { count: waypoints.length }));
      }

      // Only call showRouteWaypoints if we're in track mode (has a route)
      if (hasTask) {
        this.showRouteWaypoints();
      }
      this.updateMenu();
      
    } catch (error) {
      console.error('Error importing CUP file:', error);
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.failed_parse_cup", { error: error.message }));
    }
  }

  parseCUPWaypoints(waypointSection) {
    const lines = waypointSection.split('\n');
    const waypoints = [];
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine || trimmedLine.startsWith('name,') || trimmedLine.startsWith('*') || trimmedLine.startsWith('#')) {
        continue;
      }
      
      try {
        const waypoint = this.parseCUPWaypointLine(trimmedLine);
        if (waypoint) {
          waypoints.push(waypoint);
        }
      } catch (error) {
        console.warn('Failed to parse CUP line:', trimmedLine, error);
      }
    }
    
    return waypoints;
  }

  parseCUPWaypointLine(line) {
    const fields = this.parseCSVLine(line);
    
    if (fields.length < 5) {
      return null;
    }
    
    const name = fields[0] || '';
    const code = fields[1] || '';
    const country = fields[2] || '';
    const latStr = fields[3] || '';
    const lonStr = fields[4] || '';
    const elevationStr = fields[5] || '0';
    
    if (!name || !latStr || !lonStr) {
      return null;
    }
    
    const lat = this.parseCUPCoordinate(latStr);
    const lon = this.parseCUPCoordinate(lonStr);
    const elevation = this.parseCUPElevation(elevationStr);
    
    if (isNaN(lat) || isNaN(lon)) {
      return null;
    }
    
    return {
      name: name,
      code: code,
      country: country,
      lat: lat,
      lon: lon,
      alt: elevation || 0
    };
  }

  parseCSVLine(line) {
    const fields = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    fields.push(current.trim());
    return fields.map(field => field.replace(/^"(.*)"$/, '$1'));
  }

  parseCUPCoordinate(coordStr) {
    if (!coordStr) return NaN;
    
    coordStr = coordStr.trim();
    
    // Handle decimal degrees format
    const decimalMatch = coordStr.match(/^(-?\d+\.?\d*)$/);
    if (decimalMatch) {
      return parseFloat(decimalMatch[1]);
    }
    
    // Handle DDMM.MMM[NSEW] format (common in CUP files)
    const ddmmMatch = coordStr.match(/^(\d{2,3})(\d{2}\.\d+)([NSEW])$/i);
    if (ddmmMatch) {
      const degrees = parseInt(ddmmMatch[1]);
      const minutes = parseFloat(ddmmMatch[2]);
      const direction = ddmmMatch[3].toUpperCase();
      
      let decimal = degrees + (minutes / 60);
      
      if (direction === 'S' || direction === 'W') {
        decimal = -decimal;
      }
      
      return decimal;
    }
    
    // Handle DD:MM:SS[NSEW] format
    const dmsMatch = coordStr.match(/^(\d+):(\d+):(\d+(?:\.\d+)?)\s*([NSEW])$/i);
    if (dmsMatch) {
      const degrees = parseInt(dmsMatch[1]);
      const minutes = parseInt(dmsMatch[2]);
      const seconds = parseFloat(dmsMatch[3]);
      const direction = dmsMatch[4].toUpperCase();
      
      let decimal = degrees + (minutes / 60) + (seconds / 3600);
      
      if (direction === 'S' || direction === 'W') {
        decimal = -decimal;
      }
      
      return decimal;
    }
    
    // Handle DD:MM.MMM[NSEW] format
    const dmMatch = coordStr.match(/^(\d+):(\d+(?:\.\d+)?)\s*([NSEW])$/i);
    if (dmMatch) {
      const degrees = parseInt(dmMatch[1]);
      const minutes = parseFloat(dmMatch[2]);
      const direction = dmMatch[3].toUpperCase();
      
      let decimal = degrees + (minutes / 60);
      
      if (direction === 'S' || direction === 'W') {
        decimal = -decimal;
      }
      
      return decimal;
    }
    
    return NaN;
  }

  parseCUPElevation(elevStr) {
    if (!elevStr) return 0;
    
    elevStr = elevStr.trim();
    const match = elevStr.match(/^(-?\d+(?:\.\d+)?)/);
    
    if (match) {
      const value = parseFloat(match[1]);
      
      if (elevStr.toLowerCase().includes('ft')) {
        return Math.round(value * 0.3048);
      }
      
      return Math.round(value);
    }
    
    return 0;
  }

  // XCTrack Format 2 coordinate encoding
  encodeCoordinateWithAltRadius(lat, lon, alt, radius) {
    // Format matches testformat2.xctsk: "lat,lon,alt,radius" (latitude first!)
    return `${lat.toFixed(6)},${lon.toFixed(6)},${alt.toFixed(1)},${radius}`;
  }

  exportXCTSK() {
    if (!this.trackMode || !this.trackHandles || this.trackHandles.length < 2) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.task_needs_2_waypoints"));
      return;
    }

    // Get route waypoints in order
    const routeWaypoints = this.trackHandles
      .filter(handle => handle.attachedWaypointIndex !== null)
      .map(handle => ({
        index: handle.attachedWaypointIndex,
        waypoint: this.waypoints[handle.attachedWaypointIndex],
        marker: this.markers[handle.attachedWaypointIndex]
      }));

    if (routeWaypoints.length < 2) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.task_needs_2_waypoints"));
      return;
    }

    const startTimeBegins = document.getElementById('startTimeBeginsInput')?.value || '';
    const goalDeadline = document.getElementById('goalDeadlineTimeInput')?.value || '';

    // Convert times to UTC format (HH:MM:SSZ) to match working example
    const formatTimeForXCTSK = (dateTimeString) => {
      if (!dateTimeString) return '';
      const date = new Date(dateTimeString);
      return date.toISOString().substring(11, 19) + 'Z'; // Extract HH:MM:SSZ
    };

    // XCTrack Task Definition Format 1 (matching working example)
    const taskData = {
      earthModel: "WGS84",
      taskType: "CLASSIC",
      version: 1,
      turnpoints: routeWaypoints.map((item) => {
        const name = item.marker.label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1];
        const altitude = this.getWaypointAltitude(item.index);
        const radius = item.marker.circle ? Math.round(item.marker.circle.getRadius()) : 400;
        const waypointType = item.marker.marker.waypointType;

        const turnpoint = {
          radius: radius,
          waypoint: {
            altSmoothed: altitude,
            description: "",
            lat: item.waypoint.lat,
            lon: item.waypoint.lng,
            name: name
          }
        };

        // Add type for special waypoints (matching working example format)
        if (waypointType === 'sss') {
          turnpoint.type = 'SSS';
        } else if (waypointType === 'ess') {
          turnpoint.type = 'ESS';
        }

        return turnpoint;
      })
    };

    // Always add SSS configuration (required by XCTrack parser)
    taskData.sss = {
      direction: "EXIT",
      timeGates: startTimeBegins ? [formatTimeForXCTSK(startTimeBegins)] : ["08:00:00Z"],
      type: "RACE"
    };

    // Always add goal configuration (required by XCTrack parser)
    taskData.goal = {
      deadline: goalDeadline ? formatTimeForXCTSK(goalDeadline) : "18:00:00Z",
      type: "CYLINDER"
    };

    const blob = new Blob([JSON.stringify(taskData)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const filename = document.getElementById('taskNameInput')?.value || 'task';
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\s+/g, '_')}.xctsk`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  formatLatLonForCUP(lat, lon) {
    // Convert decimal degrees to DDMM.MMM[NSEW] format
    const formatCoordinate = (coord, isLongitude) => {
      const abs = Math.abs(coord);
      const degrees = Math.floor(abs);
      const minutes = (abs - degrees) * 60;

      let direction;
      if (isLongitude) {
        direction = coord >= 0 ? 'E' : 'W';
      } else {
        direction = coord >= 0 ? 'N' : 'S';
      }

      const degreesStr = isLongitude ? degrees.toString().padStart(3, '0') : degrees.toString().padStart(2, '0');
      const minutesStr = minutes.toFixed(3).padStart(6, '0');

      return `${degreesStr}${minutesStr}${direction}`;
    };

    return {
      lat: formatCoordinate(lat, false),
      lon: formatCoordinate(lon, true)
    };
  }

  exportCUP() {
    // Check if we have waypoints to export
    if (!this.waypoints || this.waypoints.length === 0) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.no_waypoints_to_export"));
      return;
    }

    // Determine if we're exporting a route or just waypoints
    const isRouteMode = this.trackMode && this.trackHandles && this.trackHandles.length >= 2;

    let routeWaypoints = [];
    if (isRouteMode) {
      // Get route waypoints in order (for the task line)
      routeWaypoints = this.trackHandles
        .filter(handle => handle.attachedWaypointIndex !== null)
        .map(handle => ({
          index: handle.attachedWaypointIndex,
          waypoint: this.waypoints[handle.attachedWaypointIndex],
          marker: this.markers[handle.attachedWaypointIndex]
        }));

      if (routeWaypoints.length < 2) {
        ToastNotification.make(I18n.t("website.contest_task.editor.errors.task_needs_2_waypoints"));
        return;
      }
    }

    // Get ALL waypoints (both route and markers)
    const allWaypointsData = this.waypoints.map((waypoint, index) => ({
      index,
      waypoint,
      marker: this.markers[index]
    }));

    // Create CUP header
    let cupContent = 'name,code,country,lat,lon,elev,style,rwdir,rwlen,freq,desc\n';

    // Add ALL waypoints to the CUP file
    allWaypointsData.forEach((item, index) => {
      const name = item.marker.label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1];
      const code = item.marker.marker.waypointId || `WP${index + 1}`;
      const coords = this.formatLatLonForCUP(item.waypoint.lat, item.waypoint.lng);
      const altitude = this.getWaypointAltitude(item.index);

      cupContent += `"${name}",${code},,${coords.lat},${coords.lon},${altitude}m,1,,,,\n`;
    });

    // Only add task section if we're in route mode
    if (isRouteMode) {
      // Add task section
      cupContent += '-----Related Tasks-----\n';

      // Create task line with waypoint names from route waypoints only
      const taskNames = routeWaypoints.map((item) => {
        const name = item.marker.label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1];
        return name;
      });
      cupContent += `"null","","${taskNames.join('","')}",""\n`;

      // Add task options
      cupContent += 'Options,StartOnEntry=True\n';

      // Add observation zones with radii for route waypoints only
      routeWaypoints.forEach((item, index) => {
        const radius = item.marker.circle ? Math.round(item.marker.circle.getRadius()) : 1000;
        cupContent += `ObsZone=${index},R1=${radius}m\n`;
      });
    }

    // Download the file
    const blob = new Blob([cupContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const defaultName = isRouteMode ? 'task' : 'waypoints';
    const filename = (isRouteMode && document.getElementById('taskNameInput')?.value) || defaultName;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\s+/g, '_')}.cup`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async generateQRCode() {
    if (!this.trackMode || !this.trackHandles || this.trackHandles.length < 2) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.task_needs_2_waypoints"));
      return;
    }

    // Get route waypoints in order
    const routeWaypoints = this.trackHandles
      .filter(handle => handle.attachedWaypointIndex !== null)
      .map(handle => ({
        index: handle.attachedWaypointIndex,
        waypoint: this.waypoints[handle.attachedWaypointIndex],
        marker: this.markers[handle.attachedWaypointIndex]
      }));

    if (routeWaypoints.length < 2) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.task_needs_2_waypoints"));
      return;
    }

    const taskName = document.getElementById('taskNameInput')?.value || 'Contest Task';
    const startTimeBegins = document.getElementById('startTimeBeginsInput')?.value || '';
    const goalDeadline = document.getElementById('goalDeadlineTimeInput')?.value || '';

    // Convert times to UTC format (HH:MM:SSZ) to match working example
    const formatTimeForXCTSK = (dateTimeString) => {
      if (!dateTimeString) return '';
      const date = new Date(dateTimeString);
      return date.toISOString().substring(11, 19) + 'Z'; // Extract HH:MM:SSZ
    };

    // Create XCTrack Task Definition Format 1 for QR code (matching working example)
    const taskData = {
      earthModel: "WGS84",
      taskType: "CLASSIC",
      version: 1,
      turnpoints: routeWaypoints.map((item) => {
        const name = item.marker.label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1];
        const altitude = this.getWaypointAltitude(item.index);
        const radius = item.marker.circle ? Math.round(item.marker.circle.getRadius()) : 400;
        const waypointType = item.marker.marker.waypointType;

        const turnpoint = {
          radius: radius,
          waypoint: {
            altSmoothed: altitude,
            description: "",
            lat: item.waypoint.lat,
            lon: item.waypoint.lng,
            name: name
          }
        };

        // Add type for special waypoints (matching working example format)
        if (waypointType === 'sss') {
          turnpoint.type = 'SSS';
        } else if (waypointType === 'ess') {
          turnpoint.type = 'ESS';
        }

        return turnpoint;
      })
    };

    // Always add SSS configuration (required by XCTrack parser)
    taskData.sss = {
      direction: "EXIT",
      timeGates: startTimeBegins ? [formatTimeForXCTSK(startTimeBegins)] : ["08:00:00Z"],
      type: "RACE"
    };

    // Always add goal configuration (required by XCTrack parser)
    taskData.goal = {
      deadline: goalDeadline ? formatTimeForXCTSK(goalDeadline) : "18:00:00Z",
      type: "CYLINDER"
    };

    // XCTrack QR code format requires "XCTSK:" prefix
    const qrData = "XCTSK:" + JSON.stringify(taskData);

    // Create a simple QR code using a service (you might want to use a proper QR library)
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrData)}`;

    // Save QR code to backend if we have a task ID
    if (this.taskId) {
      try {
        const response = await fetch(`${API_HOST}/v1/contest_task/${this.taskId}/save_qr_code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-STL-Token': cookies.get('STL-Token')
          },
          body: JSON.stringify({ qr_code_url: qrUrl })
        });

        const data = await response.json();
        if (data.success) {
          console.log('QR code saved successfully:', data.qr_code_url);
        } else {
          console.error('Failed to save QR code:', data.error);
        }
      } catch (error) {
        console.error('Error saving QR code:', error);
      }
    }

    // Open QR code in new window
    const qrWindow = window.open('', '_blank');
    qrWindow.document.write(`
      <html>
        <head><title>XCTrack Task QR Code - ${taskName}</title></head>
        <body style="text-align: center; padding: 20px;">
          <h2>${taskName}</h2>
          <p>Distance: ${this.computeTrackDistance()}</p>
          <p>Route Waypoints: ${routeWaypoints.length}</p>
          <img src="${qrUrl}" alt="XCTrack Task QR Code" style="border: 1px solid #ccc; padding: 10px;">
          <p style="font-size: 12px; margin-top: 20px;">Scan this QR code with XCTrack to import the task</p>
          <p style="font-size: 12px;"><a href="https://www.sportstracklive.com">Generated by SportsTrackLive</a></p>
        </body>
      </html>
    `);
  }

  showExportDialog() {
    // Check if we have any waypoints to export
    if (!this.waypoints || this.waypoints.length === 0) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.no_waypoints_found"));
      return;
    }

    // Determine if we're in route mode (task export) or waypoint mode
    const isRouteMode = this.trackMode && this.trackHandles && this.trackHandles.length >= 2;
    const exportTitle = isRouteMode ? I18n.t("website.contest_task.editor.export.title_task") : I18n.t("website.contest_task.editor.export.title_waypoints");
    const exportDescription = isRouteMode ? I18n.t("website.contest_task.editor.export.description_task") : I18n.t("website.contest_task.editor.export.description_waypoints");

    L.popup({
      closeButton: true,
      autoClose: true,
      className: 'export-format-popup',
      maxWidth: 350
    })
      .setLatLng(this.map.getCenter())
      .setContent(`
        <div class="container-fluid p-0">
          <h2 class="mb-3">${exportTitle}</h2>
          <p class="mb-3">${exportDescription}</p>
          <div class="d-flex gap-1 mb-2 justify-content-end">
            ${isRouteMode ? `<button id="exportXCTSKBtn" class="btn btn-primary btn-sm flex-fill mr-1">
              <i class="fas fa-file-code mr-1"></i>XCTSK
            </button>` : ''}
            <button id="exportCUPBtn" class="btn btn-success btn-sm flex-fill mr-1">
              <i class="fas fa-map mr-1"></i>CUP
            </button>
            ${isRouteMode ? `<button id="exportQRBtn" class="btn btn-info btn-sm flex-fill mr-1">
              <i class="fas fa-qrcode mr-1"></i>QR Code
            </button>` : ''}
          </div>
        </div>
      `)
      .openOn(this.map);

    setTimeout(() => {
      const xctsKBtn = document.getElementById('exportXCTSKBtn');
      const cupBtn = document.getElementById('exportCUPBtn');
      const qrBtn = document.getElementById('exportQRBtn');
      const cancelBtn = document.getElementById('cancelExportBtn');

      if (xctsKBtn) {
        xctsKBtn.addEventListener('click', () => {
          this.map.closePopup();
          this.exportXCTSK();
        });
      }

      if (cupBtn) {
        cupBtn.addEventListener('click', () => {
          this.map.closePopup();
          this.exportCUP();
        });
      }

      if (qrBtn) {
        qrBtn.addEventListener('click', () => {
          this.map.closePopup();
          this.generateQRCode();
        });
      }

      if (cancelBtn) {
        cancelBtn.addEventListener('click', () => {
          this.map.closePopup();
        });
      }
    }, 10);
  }

  buildContestTaskData() {
    const taskDate = new Date();
    taskDate.setDate(taskDate.getDate() + 1);
    taskDate.setHours(10, 0, 0, 0);
  
    const startTimeBegins = new Date(taskDate);
    const startTimeEnds = new Date(taskDate);
    startTimeEnds.setHours(12, 0, 0, 0);
    const goalDeadlineTime = new Date(taskDate);
    goalDeadlineTime.setHours(18, 0, 0, 0);
    
    let orderedWaypoints;

    if (this.trackMode && this.trackHandles) {
      // Get waypoints that are part of the route (in order)
      const routeWaypoints = this.trackHandles
        .filter(handle => handle.attachedWaypointIndex !== null)
        .map(handle => ({
          index: handle.attachedWaypointIndex,
          waypoint: this.waypoints[handle.attachedWaypointIndex],
          marker: this.markers[handle.attachedWaypointIndex],
          isInRoute: true
        }));

      // Get waypoints that are markers only (not part of route)
      const markerOnlyWaypoints = [];
      for (let i = 0; i < this.waypoints.length; i++) {
        const isInRoute = this.trackHandles.some(handle => handle.attachedWaypointIndex === i);
        if (!isInRoute && this.markers[i]) {
          markerOnlyWaypoints.push({
            index: i,
            waypoint: this.waypoints[i],
            marker: this.markers[i],
            isInRoute: false
          });
        }
      }

      // Combine route waypoints first, then marker-only waypoints
      orderedWaypoints = [...routeWaypoints, ...markerOnlyWaypoints];
    } else {
      // Not in track mode, include all waypoints
      orderedWaypoints = this.waypoints.map((waypoint, index) => ({
        index,
        waypoint,
        marker: this.markers[index],
        isInRoute: false
      }));
    }

    const points = orderedWaypoints.map((item, index, array) => {
      const { waypoint, marker, isInRoute } = item;
      const markerType = marker.marker.waypointType;

      const pointTypes = getPointTypes();
      let pointItemType = pointTypes.regular.value;
      if (pointTypes[markerType]) {
        pointItemType = pointTypes[markerType].value;
      }

      return {
        point_item_type: pointItemType,
        name: marker.label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1],
        description: `Task waypoint ${index + 1}`,
        latitude: waypoint.lat.toString(),
        longitude: waypoint.lng.toString(),
        altitude: this.getWaypointAltitude(item.index),
        radius: marker.circle ? marker.circle.getRadius() : 400,
        is_route_waypoint: isInRoute,
        waypoint_id: marker.marker.waypointId || null
      };
    });

    const categorySelect = document.getElementById('contest_task_activity');
    const categoryId = categorySelect ? parseInt(categorySelect.value) :
                       parseInt(document.querySelector('meta[name="category-id"]')?.content || 1);

    const groupSelect = document.getElementById('contest_task_group');
    const groupId = groupSelect && groupSelect.value ? parseInt(groupSelect.value) : null;

    const data = {
      contest_task: {
        name: document.getElementById('taskNameInput')?.value?.trim() || "",
        category: categoryId,
        group_id: groupId,
        start_time_begins: this.dateFieldsEnabled ? (document.getElementById('startTimeBeginsInput')?.value || startTimeBegins.toISOString()) : null,
        start_time_ends: this.dateFieldsEnabled ? (document.getElementById('startTimeEndsInput')?.value || startTimeEnds.toISOString()) : null,
        goal_deadline_time: this.dateFieldsEnabled ? (document.getElementById('goalDeadlineTimeInput')?.value || goalDeadlineTime.toISOString()) : null,
        target_speed: parseFloat(document.getElementById('targetSpeedInput')?.value) || 7.0,
        points: points
      }
    };

    return data;
  }

  // Check if changes affect scoring (waypoint positions, radius, order, or scoring attributes changed)
  hasScoringChanges() {
    if (!this.isEditMode || !this.originalTaskData) {
      return false; // New tasks don't have scoring changes
    }

    const currentData = this.buildContestTaskData().contest_task;

    // Helper to normalize time values (handle both datetime strings and null)
    const normalizeTime = (time) => {
      if (!time) return null;
      // Remove milliseconds and Z suffix for comparison
      return time.split('.')[0].replace('Z', '');
    };

    // Check if scoring-affecting attributes changed
    const categoryChanged = currentData.category !== this.originalTaskData.category;
    const targetSpeedChanged = Math.abs(currentData.target_speed - this.originalTaskData.target_speed) > 0.01;

    const startTimeBeginsChanged = normalizeTime(currentData.start_time_begins) !==
                                   normalizeTime(this.originalTaskData.start_time_begins);
    const startTimeEndsChanged = normalizeTime(currentData.start_time_ends) !==
                                 normalizeTime(this.originalTaskData.start_time_ends);
    const goalDeadlineChanged = normalizeTime(currentData.goal_deadline_time) !==
                                normalizeTime(this.originalTaskData.goal_deadline_time);

    if (categoryChanged || targetSpeedChanged || startTimeBeginsChanged ||
        startTimeEndsChanged || goalDeadlineChanged) {
      console.log('Scoring attribute changed:', {
        categoryChanged,
        targetSpeedChanged,
        startTimeBeginsChanged,
        startTimeEndsChanged,
        goalDeadlineChanged
      });
      return true;
    }

    // Check if route waypoints changed (positions, radius, order, or count)
    // Don't sort - order matters for the route!
    const oldRoutePoints = this.originalTaskData.points.map(p => ({
      latitude: parseFloat(p.latitude).toFixed(6),
      longitude: parseFloat(p.longitude).toFixed(6),
      radius: parseInt(p.radius),
      is_route: p.is_route_waypoint === true
    }));

    const newRoutePoints = currentData.points.map(p => ({
      latitude: parseFloat(p.latitude).toFixed(6),
      longitude: parseFloat(p.longitude).toFixed(6),
      radius: parseInt(p.radius),
      is_route: p.is_route_waypoint === true
    }));

    // Compare as JSON strings to detect any differences
    const routeChanged = JSON.stringify(oldRoutePoints) !== JSON.stringify(newRoutePoints);

    if (routeChanged) {
      console.log('Route changed:', {
        old: oldRoutePoints,
        new: newRoutePoints
      });
    }

    return routeChanged;
  }

  saveContestTask() {
    // Check if user is logged in
    if (!window.data?.user_logged_in) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.login_required"));
      return;
    }

    // Check if user is premium
    if (!window.data?.user_is_premium) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.premium_required"));
      return;
    }

    if (this.waypoints.length < 2) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.task_needs_2_waypoints"));
      return;
    }

    // Validate date inputs before saving
    if (!this.validateTimeInputs(true)) {
      return;
    }

    // Check if there are existing challengers and scoring changes
    const hasChallengers = window.data?.has_challengers || false;
    const hasScoringChanges = this.hasScoringChanges();

    // Only show warning if there are challengers AND scoring-affecting changes
    if (hasChallengers && this.isEditMode && hasScoringChanges) {
      this.confirmationModal.displayConfirm(
        I18n.t("website.contest_task.editor.warnings.save_with_challengers_title"),
        I18n.t("website.contest_task.editor.warnings.save_with_challengers_message"),
        {
          positive_btn: {
            label: I18n.t("website.contest_task.editor.warnings.save_with_challengers_confirm"),
            type: "success",
            callback: () => {
              this.performSave();
            }
          },
          negative_btn: {
            label: I18n.t("website.contest_task.editor.warnings.save_with_challengers_cancel"),
            callback: () => {
              // Just close the modal, no action needed
            }
          }
        }
      );
    } else {
      this.performSave();
    }
  }

  performSave() {
    const data = this.buildContestTaskData();
    const url = this.isEditMode ?
      `${API_HOST}/v1/contest_task/${this.taskId}` :
      `${API_HOST}/v1/contest_task/`;

    fetch(url, {
      method: this.isEditMode ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-STL-Token': cookies.get('STL-Token')
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(errorData => {
          // Handle validation errors from Rails
          if (errorData.name) {
            // Rails validation errors come as { field: ['error1', 'error2'] }
            const errorMessage = Array.isArray(errorData.name) ? errorData.name.join(', ') : errorData.name;
            throw new Error(errorMessage);
          } else if (errorData['contest_task_point_items.name']) {
            // Handle waypoint name validation errors
            const waypointErrors = errorData['contest_task_point_items.name'];
            const errorMessage = Array.isArray(waypointErrors) ? waypointErrors.join(', ') : waypointErrors;
            throw new Error(errorMessage);
          } else if (errorData.error) {
            throw new Error(errorData.error);
          } else {
            throw new Error('Failed to save task. Please try again.');
          }
        });
      }
      return response.json();
    })
    .then(result => {
      if (result.id) {
        if (!this.isEditMode) {
          window.location.href = `/contest_task/${result.id}`;
        } else {
          ToastNotification.make(I18n.t("website.contest_task.editor.success.task_updated"));
        }
      } else {
        throw new Error('Invalid response format');
      }
    })
    .catch(error => {
      console.error('Error saving task:', error);
      ToastNotification.make(error.message || I18n.t("website.contest_task.editor.errors.failed_save_task"));

      if (error.message && (error.message.includes('name') || error.message.includes('inappropriate') || error.message.includes('5 character'))) {
        document.getElementById('taskNameInput')?.focus();
      }
    });
  }

  showRouteWaypoints() {
    // Only show route waypoints table if we're in track mode or have route waypoints
    if (!this.trackMode && (!this.trackHandles || this.trackHandles.length === 0)) {
      return;
    }

    const menuContent = document.getElementById('menu-content');
    menuContent.dataset.active = 'waypoints';

    const oldSettings = {
      taskName: document.getElementById('taskNameInput')?.value,
      categoryId: document.getElementById('contest_task_activity')?.value || window.data?.category_id,
      groupId: document.getElementById('contest_task_group')?.value || window.data?.group_id || null,
      startTimeBegins: document.getElementById('startTimeBeginsInput')?.value,
      startTimeEnds: document.getElementById('startTimeEndsInput')?.value,
      goalDeadline: document.getElementById('goalDeadlineTimeInput')?.value,
      targetSpeed: document.getElementById('targetSpeedInput')?.value
    };

    menuContent.innerHTML = "";
    this.updateTrackInfo();

    if (this.trackMode) {
      let settingsDiv = document.createElement("div");
      settingsDiv.className = "task-settings visible rounded";

      // Build activities options HTML
      const activities = window.data?.activities || [];
      const hasChallengers = window.data?.has_challengers || false;
      const categoryOptions = activities.map(activity =>
        `<option value="${activity.id}" ${activity.id == oldSettings.categoryId ? 'selected' : ''} data-description="${activity.description}">${activity.name}</option>`
      ).join('');

      // Build groups options HTML (only if user has groups)
      const hasGroups = this.userGroups.length > 0;
      const groupOptions = hasGroups ? [
        `<option value="">${I18n.t("website.contest_task.editor.form.no_group")}</option>`,
        ...this.userGroups.map(group =>
          `<option value="${group.id}" ${group.id == oldSettings.groupId ? 'selected' : ''}>${group.name}</option>`
        )
      ].join('') : '';

      settingsDiv.innerHTML = `
        <form class="container-fluid pt-2">
          <div class="form-group row align-items-center mb-0">
            <label for="taskNameInput" class="col-5 col-form-label fs-13">${I18n.t("website.contest_task.editor.form.task_name")}:</label>
            <div class="col-7">
              <input id="taskNameInput" type="text" class="form-control form-control-sm fs-13" value="${oldSettings.taskName || ''}">
            </div>
          </div>
          <div class="form-group row align-items-center mb-0">
            <label for="contest_task_activity" class="col-5 col-form-label fs-13">${I18n.t("website.contest_task.editor.form.activity")}:</label>
            <div class="col-7">
              <select id="contest_task_activity" class="form-control form-control-sm fs-13" name="category" ${hasChallengers ? 'disabled' : ''} title="${hasChallengers ? 'Cannot change activity: challengers have already submitted results' : ''}">
                ${categoryOptions}
              </select>
            </div>
          </div>
          ${hasGroups ? `
          <div class="form-group row align-items-center mb-0">
            <label for="contest_task_group" class="col-5 col-form-label fs-13">${I18n.t("website.contest_task.editor.form.assigned_group")}:</label>
            <div class="col-7">
              <select id="contest_task_group" class="form-control form-control-sm fs-13" name="group">
                ${groupOptions}
              </select>
            </div>
          </div>
          ` : ''}
          <div class="form-group row align-items-center mb-0">
            <label for="startTimeBeginsInput" class="col-5 col-form-label fs-13">${I18n.t("website.contest_task.editor.form.start_time_begins")}:</label>
            <div class="col-7">
              <input id="startTimeBeginsInput" type="datetime-local" class="form-control form-control-sm fs-13" value="${oldSettings.startTimeBegins || ''}">
            </div>
          </div>
          <div class="form-group row align-items-center mb-0">
            <label for="startTimeEndsInput" class="col-5 col-form-label fs-13">${I18n.t("website.contest_task.editor.form.start_time_ends")}:</label>
            <div class="col-7">
              <input id="startTimeEndsInput" type="datetime-local" class="form-control form-control-sm fs-13" value="${oldSettings.startTimeEnds || ''}">
            </div>
          </div>
          <div class="form-group row align-items-center mb-0">
            <label for="goalDeadlineTimeInput" class="col-5 col-form-label fs-13">${I18n.t("website.contest_task.editor.form.goal_deadline_time")}:</label>
            <div class="col-7">
              <input id="goalDeadlineTimeInput" type="datetime-local" class="form-control form-control-sm fs-13" value="${oldSettings.goalDeadline || ''}">
            </div>
          </div>
          <div class="form-group row align-items-center mb-0">
            <label for="targetSpeedInput" class="col-5 col-form-label fs-13">${I18n.t("website.contest_task.editor.form.target_speed")}:</label>
            <div class="col-7">
              <input id="targetSpeedInput" type="number" step="0.1" min="0" class="form-control form-control-sm fs-13" value="${oldSettings.targetSpeed || '7.0'}">
            </div>
          </div>
        </form>
      `;
      menuContent.appendChild(settingsDiv);

      // Add event listener for category changes to refetch groups
      const categorySelect = document.getElementById('contest_task_activity');
      if (categorySelect) {
        categorySelect.addEventListener('change', async (e) => {
          const newCategoryId = parseInt(e.target.value);
          if (window.data) {
            window.data.category_id = newCategoryId;
          }
          // Refetch groups for the new category
          await this.fetchUserGroups();
          // Update the group dropdown dynamically instead of rebuilding the entire form
          this.updateGroupDropdown();
        });
      }

      // Hide date fields by default, unless they already have values or dateFieldsEnabled is true
      if (!this.dateFieldsEnabled) {
        const startTimeBeginsRow = document.getElementById('startTimeBeginsInput')?.closest('.form-group');
        const startTimeEndsRow = document.getElementById('startTimeEndsInput')?.closest('.form-group');
        const goalDeadlineRow = document.getElementById('goalDeadlineTimeInput')?.closest('.form-group');
        if (startTimeBeginsRow) startTimeBeginsRow.style.display = 'none';
        if (startTimeEndsRow) startTimeEndsRow.style.display = 'none';
        if (goalDeadlineRow) goalDeadlineRow.style.display = 'none';
      }
    }

    let table = document.createElement("table");
    table.classList.add("contest");
    let thead = document.createElement("thead");
    let headerRow = document.createElement("tr");
   
    [
      "",
      I18n.t("website.contest_task.editor.table.name"),
      I18n.t("website.contest_task.editor.table.id"),
      I18n.t("website.contest_task.editor.table.distance"),
      I18n.t("website.contest_task.editor.table.radius"),
      ""
    ].forEach(text => {
      let th = document.createElement("th");
      th.textContent = text;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);
    
    let tbody = document.createElement("tbody");
    tbody.id = 'waypoints-tbody';
    
    // Only show waypoints that are part of the route (have track handles)
    const routeWaypointIndices = [];
    if (this.trackMode && this.trackHandles) {
      this.trackHandles.forEach((handle) => {
        if (handle.attachedWaypointIndex !== null && !handle.autoInserted) {
          routeWaypointIndices.push(handle.attachedWaypointIndex);
        }
      });
    } else {
      // If not in track mode, show all waypoints
      routeWaypointIndices.push(...Array.from({ length: this.waypoints.length }, (_, i) => i));
    }
    const waypointIndices = routeWaypointIndices;

    waypointIndices.forEach((i) => {
      let row = document.createElement("tr");
      const marker = this.markers[i].marker;
      const waypointType = marker.waypointType;

      const canEditTask = window.data?.user_can_save_task;
      const isDraggable = waypointType !== 'take_off' && waypointType !== 'goal' && canEditTask;
      row.dataset.waypointIndex = i;
      row.dataset.type = waypointType;
      row.draggable = isDraggable;

      // Add blink animation to newly added waypoint
      if (this.lastToggledWaypointIndex === i) {
        // row.classList.add('blink_helper');
        // Clear the flag after adding the animation
        setTimeout(() => {
          this.lastToggledWaypointIndex = null;
        }, 1000);
      }

      let tdDrag = document.createElement("td");

      // Only show drag handle if user can edit
      if (canEditTask) {
        let dragIcon = document.createElement("i");
        dragIcon.className = "fas fa-arrows-alt drag-handle";
        dragIcon.style.color = isDraggable ? "white" : "#666";
        tdDrag.style.cursor = isDraggable ? "move" : "not-allowed";
        tdDrag.appendChild(dragIcon);
      } else {
        // Show empty cell for non-owners
        tdDrag.style.width = "20px";
      }

      row.appendChild(tdDrag);
      
      let tdName = document.createElement("td");
      const labelText = this.markers[i].label.getIcon().options.html.match(/<div.*?>(.*?)<\/div>/)[1];
      tdName.textContent = labelText;
      tdName.style.whiteSpace = "nowrap";
      tdName.style.overflow = "hidden";
      tdName.style.textOverflow = "ellipsis";
      tdName.style.maxWidth = "150px";
      row.appendChild(tdName);


      let tdId = document.createElement("td");
      tdId.textContent = marker.waypointId || "";
      row.appendChild(tdId);


      let tdDistance = document.createElement("td");
      const currentPositionInRoute = waypointIndices.indexOf(i);
      if (currentPositionInRoute === 0) {
        tdDistance.innerHTML = `<code>-</code>`;
      } else {
        // Calculate distance to the previous waypoint in the route
        const prevWaypointIndex = waypointIndices[currentPositionInRoute - 1];
        const dist = this.waypoints[prevWaypointIndex].distanceTo(this.waypoints[i]);
        if (dist >= 2000) {
          tdDistance.innerHTML = `${Math.round(dist / 1000)} km`;
        } else {
          tdDistance.innerHTML = `${Math.round(dist)} m`;
        }
      }
      row.appendChild(tdDistance);

      let tdRadius = document.createElement("td");
      let currentRadius = this.markers[i].circle ? this.markers[i].circle.getRadius() : 0;
      tdRadius.innerHTML = `<code style="color: #fff">${Math.round(currentRadius)}m</code>`;
      row.appendChild(tdRadius);
      
      let tdAction = document.createElement("td");
      if (waypointType !== 'start' && waypointType !== 'end') {
        let deleteIcon = document.createElement("i");
        deleteIcon.className = "fas fa-trash text-white";
        deleteIcon.style.cursor = "pointer";
        deleteIcon.addEventListener("click", () => {
          this.deleteWaypoint(i);
        });
        tdAction.appendChild(deleteIcon);
      }
      row.appendChild(tdAction);

      // Add click event to blink circle on map
      row.addEventListener('click', (e) => {
        // Don't trigger blink if clicking on delete icon or drag handle
        if (e.target.closest('.fa-trash') || e.target.closest('.fa-arrows-alt')) {
          return;
        }
        this.blinkCircle(i);
      });

      // Add double-click event to open waypoint editor
      row.addEventListener('dblclick', () => {
        this.openWaypointEditor(i);
      });
      row.style.cursor = 'pointer';

      tbody.appendChild(row);
    });
    
    table.appendChild(tbody);
    menuContent.appendChild(table);

    this.initializeTableDragAndDrop(tbody);
    this.attachStartTimeSync();
  }

  initializeTableDragAndDrop(tbody) {
    // Don't initialize drag-and-drop for non-owners
    if (!window.data?.user_can_save_task) {
      return;
    }

    let draggedRow = null;
    let originalIndex = null;

    tbody.addEventListener('dragstart', (e) => {
      draggedRow = e.target.closest('tr');
      if (!draggedRow) return;
      
      const type = draggedRow.dataset.type;
      if (type === 'take_off' || type === 'goal') {
        e.preventDefault();
        return;
      }
      
      originalIndex = Array.from(tbody.children).indexOf(draggedRow);
      draggedRow.classList.add('dragging');
    });

    tbody.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!draggedRow) return;

      const targetRow = e.target.closest('tr');
      if (!targetRow || targetRow === draggedRow) return;

      const rows = Array.from(tbody.children);
      const firstMoveableIndex = rows.findIndex(row => row.dataset.type !== 'take_off') + 1;
      const lastMoveableIndex = rows.length - rows.reverse().findIndex(row => row.dataset.type !== 'goal') - 1;
      rows.reverse();
      
      const targetIndex = rows.indexOf(targetRow);
      if (targetIndex < firstMoveableIndex || targetIndex > lastMoveableIndex) return;

      const rect = targetRow.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      
      if (e.clientY < midY) {
        targetRow.parentNode.insertBefore(draggedRow, targetRow);
      } else {
        targetRow.parentNode.insertBefore(draggedRow, targetRow.nextSibling);
      }
    });

    tbody.addEventListener('dragend', (e) => {
      if (!draggedRow) return;
      
      draggedRow.classList.remove('dragging');
      const newIndex = Array.from(tbody.children).indexOf(draggedRow);
      
      if (newIndex !== originalIndex) {
        this.updateTrackOrder(tbody);
      }
      
      draggedRow = null;
      originalIndex = null;
    });
  }

  updateTrackOrder(tbody) {
    if (!this.trackMode || !this.trackHandles) return;

    const newOrder = Array.from(tbody.children)
        .map(row => parseInt(row.dataset.waypointIndex))
        .filter(index => !isNaN(index));

        this.trackHandles.forEach(handle => {
        if (handle.autoInserted) {
            this.map.removeLayer(handle);
        }
    });

    const newHandles = [];
    newOrder.forEach((waypointIndex) => {
        const existingHandle = this.trackHandles.find(h => h.attachedWaypointIndex === waypointIndex);
        if (existingHandle) {
            newHandles.push(existingHandle);
        }
    });
    
    this.trackHandles = newHandles;
    
    const finalHandles = [];
    for (let i = 0; i < newHandles.length; i++) {
        finalHandles.push(newHandles[i]);
        
        
        if (i < newHandles.length - 1) {
            const currentHandle = newHandles[i];
            const nextHandle = newHandles[i + 1];
            const distance = currentHandle.getLatLng().distanceTo(nextHandle.getLatLng());
            
            if (distance > 800) {
                const midLatLng = L.latLng(
                    (currentHandle.getLatLng().lat + nextHandle.getLatLng().lat) / 2,
                    (currentHandle.getLatLng().lng + nextHandle.getLatLng().lng) / 2
                );
                const midHandle = L.marker(midLatLng, {
                    icon: handleIcon,
                    draggable: true
                }).addTo(this.map);
                midHandle.attachedWaypointIndex = null;
                midHandle.autoInserted = true;
                this.attachHandleEvents(midHandle);
                finalHandles.push(midHandle);
            }
        }
    }

    this.trackHandles = finalHandles;
    this.updateRouteDrawing();
    this.showRouteWaypoints();
  }
 
  showTracks() {
  }

  async getUserLatLngFromIP() {
    try {
      const resp = await fetch('https://ipapi.co/json/');
      if (!resp.ok) throw new Error('IP geolocation failed');
      const data = await resp.json();
      if (data.latitude && data.longitude) {
        return [parseFloat(data.latitude), parseFloat(data.longitude)];
      }
    } catch (e) {
      console.warn('IP geolocation failed:', e);
    }
    return [46.4237013, 11.8256556];
  }


  ensureMenuOpen() {
    const menuContent = document.getElementById('menu-content');
    if (!menuContent) return;
  
    if (window.bootstrap && bootstrap.Collapse) {
      if (!menuContent.classList.contains('show')) {
        bootstrap.Collapse.getOrCreateInstance(menuContent).show();
      }
      return;
    }
  
    if (window.jQuery && $(menuContent).collapse) {
      if (!$(menuContent).hasClass('show')) {
        $(menuContent).collapse('show');
      }
      return;
    }
  
    menuContent.style.display = '';
  }

  resetTask() {
    if (this.trackHandles) {
      this.trackHandles.forEach(handle => this.map.removeLayer(handle));
      this.trackHandles = [];
    }
    while (this.waypoints.length > 0) {
      this.deleteWaypoint(0);
    }

    this.renderer.clearTaskLayers();
    this.trackMode = false;
    this.updateTrackInfo();
    this.showRouteWaypoints();

    const addTrackBtn   = document.getElementById('addTrackBtn');
    const saveTaskBtn   = document.getElementById('saveTask');
    const exportTaskBtn = document.getElementById('exportTask');
    if (addTrackBtn)   addTrackBtn.style.display   = 'block';
    if (saveTaskBtn)   saveTaskBtn.style.display   = 'none';
    if (exportTaskBtn) exportTaskBtn.style.display = 'none';

    const resetTaskBtn = document.getElementById('resetTask');
    if (resetTaskBtn) resetTaskBtn.style.display = 'none';
  }

  duplicateTask() {
    if (this.waypoints.length === 0) {
      ToastNotification.make(I18n.t("website.contest_task.editor.errors.nothing_to_duplicate"));
      return;
    }

    // Clear task name
    const taskNameInput = document.getElementById('taskNameInput');
    if (taskNameInput) {
      taskNameInput.value = '';
    }

    // Clear date fields
    const startTimeBeginsInput = document.getElementById('startTimeBeginsInput');
    const startTimeEndsInput = document.getElementById('startTimeEndsInput');
    const goalDeadlineTimeInput = document.getElementById('goalDeadlineTimeInput');

    if (startTimeBeginsInput) startTimeBeginsInput.value = '';
    if (startTimeEndsInput) startTimeEndsInput.value = '';
    if (goalDeadlineTimeInput) goalDeadlineTimeInput.value = '';

    // Reset date fields enabled state
    this.dateFieldsEnabled = false;

    // Clear task ID to ensure a new task is created when saving
    this.taskId = null;
    this.isEditMode = false;

    // Show save button and other relevant buttons
    const saveTaskBtn = document.getElementById('saveTask');
    const exportTaskBtn = document.getElementById('exportTask');
    const resetTaskBtn = document.getElementById('resetTask');
    const setDateBtn = document.getElementById('setDate');
    const addTrackBtn = document.getElementById('addTrackBtn');

    if (saveTaskBtn) saveTaskBtn.style.display = 'inline-block';
    if (exportTaskBtn) exportTaskBtn.style.display = 'inline-block';
    if (resetTaskBtn) resetTaskBtn.style.display = 'inline-block';
    if (setDateBtn) setDateBtn.style.display = 'inline-block';
    if (addTrackBtn) addTrackBtn.style.display = 'none';

    // Update the UI
    this.updateTrackInfo();
    this.showRouteWaypoints();

    // Show success message
    ToastNotification.make(I18n.t("website.contest_task.editor.info.task_duplicated"));
  }

  initPoiSearch() {
    this.poiSearchTimer = null;
    this.poiCurrentFocus = -1;

    // POI search is now integrated in the unified map control
    // Just bind events to the existing input
    this.bindPoiSearchEvents();
  }

  bindPoiSearchEvents() {
    const poiInput = document.getElementById('poi-search-input');
    if (!poiInput) {
      // Retry after a short delay if input doesn't exist yet
      setTimeout(() => this.bindPoiSearchEvents(), 100);
      return;
    }

    poiInput.addEventListener('input', (e) => {
      const searchTerm = e.target.value.trim();
      
      if (this.poiSearchTimer) {
        clearTimeout(this.poiSearchTimer);
      }
      
      if (searchTerm.length > 2) {
        this.poiSearchTimer = setTimeout(() => {
          this.searchPointsOfInterest(searchTerm);
        }, 500);
      } else {
        this.closePoiResults();
      }
    });

    poiInput.addEventListener('keydown', (e) => {
      this.handlePoiKeyNavigation(e);
    });

    // Close results when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('#poi-search-container')) {
        this.closePoiResults();
      }
    });
  }

  searchPointsOfInterest(searchTerm) {
    // Get map center for location-based search
    const center = this.map.getCenter();
    
    const ajaxData = {
      url: `${API_HOST}/v1/search`,
      data: {
        search_type: 4, // Points of interest type
        search_input: searchTerm,
        latitude: center.lat,
        longitude: center.lng
      },
      method: 'GET',
      beforeSend: (request) => {
        request.setRequestHeader('X-STL-Token', cookies.get('STL-Token'));
      }
    };

    this.showPoiSearchSpinner();

    $.ajax(ajaxData)
      .done((data) => {
        this.displayPoiResults(data.points_of_interest || []);
      })
      .fail((err) => {
        console.error('POI search failed:', err);
        this.showPoiNoResults();
      });
  }

  showPoiSearchSpinner() {
    this.closePoiResults();
    
    const poiContainer = document.getElementById('poi-search-container');
    const resultsList = document.createElement('div');
    resultsList.id = 'poi-results-list';
    resultsList.className = 'poi-results autocomplete-items position-absolute';
    resultsList.innerHTML = `
      <div class="text-center p-3">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    `;
    
    poiContainer.appendChild(resultsList);
  }

  displayPoiResults(pois) {
    this.closePoiResults();
    
    if (!pois || pois.length === 0) {
      this.showPoiNoResults();
      return;
    }

    const poiContainer = document.getElementById('poi-search-container');
    const resultsList = document.createElement('div');
    resultsList.id = 'poi-results-list';
    resultsList.className = 'poi-results autocomplete-items position-absolute';
    
    pois.slice(0, 10).forEach((poi, index) => {
      // Format the POI name with altitude if available
      let displayName = `<span class="fw-600 fs-14">${poi.name}</span>`;
      if (poi.altitude && poi.altitude > 0) {
        displayName+= ` <span class="fs-13">${poi.altitude}m</span>`;
      }
      
      const resultItem = document.createElement('div');
      resultItem.className = 'poi-result-item js-list-item p-2';
      resultItem.dataset.focusIndex = index;
      resultItem.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="fas fa-map-marker-alt text-gray fs-14 mr-2"></i>
          <div>
            <div>${displayName}</div>
            <span class="fs-11">Lat: ${parseFloat(poi.latitude).toFixed(4)}, Lng: ${parseFloat(poi.longitude).toFixed(4)}</span>
          </div>
        </div>
      `;
      
      resultItem.addEventListener('click', () => {
        this.selectPoi(poi);
      });
      
      resultItem.addEventListener('mouseenter', () => {
        this.poiCurrentFocus = index;
        this.highlightPoiResult();
      });
      
      resultsList.appendChild(resultItem);
    });
    
    poiContainer.appendChild(resultsList);
  }

  showPoiNoResults() {
    this.closePoiResults();
    
    const poiContainer = document.getElementById('poi-search-container');
    const resultsList = document.createElement('div');
    resultsList.id = 'poi-results-list';
    resultsList.className = 'poi-results autocomplete-items position-absolute';
    resultsList.innerHTML = `
      <div class="p-3 text-muted text-center">
        No points of interest found
      </div>
    `;
    
    poiContainer.appendChild(resultsList);
  }

  handlePoiKeyNavigation(e) {
    const resultsList = document.getElementById('poi-results-list');
    if (!resultsList) return;
    
    const items = resultsList.querySelectorAll('.js-list-item');
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.poiCurrentFocus = (this.poiCurrentFocus + 1) % items.length;
      this.highlightPoiResult();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.poiCurrentFocus = this.poiCurrentFocus <= 0 ? items.length - 1 : this.poiCurrentFocus - 1;
      this.highlightPoiResult();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (this.poiCurrentFocus >= 0 && items[this.poiCurrentFocus]) {
        items[this.poiCurrentFocus].click();
      }
    } else if (e.key === 'Escape') {
      this.closePoiResults();
    }
  }

  highlightPoiResult() {
    const items = document.querySelectorAll('.poi-result-item');
    items.forEach((item, index) => {
      if (index === this.poiCurrentFocus) {
        item.classList.add('autocomplete-active');
      } else {
        item.classList.remove('autocomplete-active');
      }
    });
  }

  selectPoi(poi) {
    // Add the POI as a waypoint on the map
    const latlng = L.latLng(poi.latitude, poi.longitude);
    
    // Clear search input and close results
    const poiInput = document.getElementById('poi-search-input');
    if (poiInput) {
      poiInput.value = poi.name;
    }
    this.closePoiResults();
    
    // Add waypoint at POI location
    this.addWaypoint(latlng, 'waypoint');
    
    // Update the label to show POI name
    const lastMarkerIndex = this.markers.length - 1;
    if (lastMarkerIndex >= 0) {
      const newLabelIcon = L.divIcon({
        className: 'waypoint-label',
        html: `<div class="waypoint-text">${poi.name}</div>`,
        iconSize: [40, 20],
        iconAnchor: [20, 40]
      });
      this.markers[lastMarkerIndex].label.setIcon(newLabelIcon);
    }
    
    // Pan map to the POI location
    this.map.setView(latlng, Math.max(this.map.getZoom(), 12));
    
    this.showRouteWaypoints();
    this.saveHistory();
    
    // Clear the search input after selection
    setTimeout(() => {
      if (poiInput) {
        poiInput.value = '';
      }
    }, 1000);
  }

  closePoiResults() {
    const existingResults = document.getElementById('poi-results-list');
    if (existingResults) {
      existingResults.remove();
    }
    this.poiCurrentFocus = -1;
  }
}
